import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  styled,
  alpha,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchTransactions } from '../slices/transactionSlice';
import { fetchDebts } from '../slices/debtSlice';
import { addIncome, addExpense, fetchIncomes, fetchExpenses } from '../slices/recurringSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { esAdmin } from '../utils/roles';
import apiClient from '../services/api';
import { avisarRevisionesActualizadas } from '../hooks/useRevisionesPendientes';
import categoryService from '../services/categoryService';
import goldColors from '../themes/goldColors';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { detectarCandidatosRecurrentes, buildRecurringPayloadDesdeTransaccion } from '../utils/recurringDetection';
import RecurringCandidatesDialog from './RecurringCandidatesDialog';
// Iconos
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CategoryIcon from '@mui/icons-material/Category';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// Componentes estilizados
const UploadContainer = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: '8px',
  // 5 ago 2026 (Augusto: "no quiero que el usuario tenga que scroll down
  // para ver los detalles del import"): padding reducido de 4 a 2.5 para
  // que el paso 1 (aviso de cuota + esta zona) quepa sin scroll.
  padding: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
  }
}));
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderRadius: '8px 8px 0 0',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  // 25 ago 2026: fila con posible transacción duplicada (ver
  // posible_duplicado, marcado por el backend en statements.py comparando
  // contra transacciones ya existentes -- monto igual, mismo tipo, fecha
  // dentro de ±3 días). Solo un tinte visual -- no bloquea nada, el
  // usuario decide caso por caso si la incluye o la destilde antes de
  // importar.
  '& .fila-posible-duplicado': {
    backgroundColor: alpha(theme.palette.warning.main, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.warning.main, 0.14),
    }
  }
}));
// OJO: acá "color" es un hex crudo (ej. "#1E88E5", ver getCategoryColor),
// no una palabra clave de la paleta de MUI ('primary'/'error'/etc). Antes
// styled(Chip) reenviaba ese "color" tal cual al Chip real de MUI, que
// internamente hace theme.palette[color].contrastText para pintar su
// propio color — como "#1E88E5" no es una clave válida de la paleta,
// truena con "Cannot read properties of undefined (reading
// 'contrastText')" apenas se intenta pintar CUALQUIER categoría, dejando
// la pantalla en blanco (sin error visible para el usuario). shouldForwardProp
// deja usar "color" para nuestro propio CSS de abajo sin que llegue como
// prop real al Chip.
const CategoryChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ theme, color }) => ({
  margin: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  height: 'auto',
  borderRadius: '6px',
  backgroundColor: color ? alpha(color, 0.1) : alpha(theme.palette.primary.main, 0.1),
  color: color || theme.palette.primary.main,
  '& .MuiChip-icon': {
    color: 'inherit',
  }
}));
// 3 ago 2026: reemplaza al viejo parseStatementFile, que subía el PDF y
// esperaba la respuesta completa (transacciones + cuenta) dentro de la
// MISMA llamada HTTP. Para un estado de cuenta grande, la IA puede tardar
// 1-2 minutos en responder — más de lo que aguanta el proxy de Render
// antes de cortar la conexión sin avisar (el usuario veía un error
// genérico sin detalle real, aunque el backend sí sabía qué había
// pasado). Ahora subir el archivo (createStatementJob) solo crea un "job"
// en el backend y responde casi al instante con su id; el resultado real
// se pregunta aparte con pollJobUntilDone, cada pocos segundos, así que
// ninguna conexión individual queda abierta más que eso.
// override=true manda admin_override -- el backend (create_statement_job)
// solo lo respeta si además el email logueado está en ADMIN_EMAILS; para
// cualquier otro usuario el flag se ignora en silencio. Ver botón
// "Ignorar y continuar" más abajo (handleAdminIgnoreQuota).
const createStatementJob = async (file, override = false) => {
  const formData = new FormData();
  formData.append('file', file);
  if (override) formData.append('admin_override', 'true');
  const { data } = await apiClient.post('/statements/jobs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data; // { id, filename, status }
};
// 25 sep 2026 (Augusto: "hay un máximo de pdf's que se puedan subir al
// mismo tiempo?"). No lo hay, pero al elegir seis archivos PCM no decía
// nada: ni cuánto iba a tardar, ni cuántas páginas de la cuota del mes se
// iban a ir. El usuario se enteraba a mitad de camino, con tres archivos
// ya procesados y un 402 en el cuarto.
//
// Esto cuenta las páginas ANTES de subir nada, en el navegador. Es una
// lectura cruda del PDF: se buscan las entradas "/Type /Page" del árbol de
// páginas. Funciona en la mayoría de los extractos bancarios (que son PDFs
// de texto), y NO funciona cuando el PDF guarda sus objetos comprimidos en
// object streams -- ahí el patrón no aparece en claro y la cuenta da 0. Por
// eso devuelve null en vez de 0: null significa "no pude saberlo" y el
// aviso omite el número, en lugar de mentir con un cero.
//
// El backend igual cuenta las páginas de verdad al recibir cada archivo
// (_check_page_quota); esto es solo para avisar de antemano.
const contarPaginasPdf = async (file) => {
  try {
    const buffer = await file.arrayBuffer();
    // latin1 mapea byte a byte, así que no rompe los binarios embebidos ni
    // desplaza posiciones (utf-8 sí lo haría).
    const texto = new TextDecoder('latin1').decode(buffer);
    // [^s] descarta "/Type /Pages", que es el nodo padre del árbol, no una
    // página. El \s* cubre "/Type/Page" (sin espacio), que es lo habitual.
    const encontradas = texto.match(/\/Type\s*\/Page[^s]/g);
    const total = encontradas ? encontradas.length : 0;
    return total > 0 ? total : null;
  } catch {
    return null;
  }
};
// 5 ago 2026: extraído de handleProcessFiles para poder reusarlo también
// desde handleAdminIgnoreQuota (el botón admin recalcula la revisión
// sobre la lista de jobs completa, no solo el archivo que reintentó).
const mergeJobsForReview = (jobsList) => {
  let nextId = 1;
  const mergedTransactions = [];
  const accounts = [];
  jobsList.forEach((job, jobIdx) => {
    if (job.status !== 'done') return;
    if (job.account) accounts.push({ ...job.account, _fileName: job.fileName, _jobIndex: jobIdx, _avisos: job.avisos || [] });
    (job.transactions || []).forEach((tx) => {
      // 5 ago 2026: switch "Recurrente" por fila (ver columna `es_recurrente`
      // más abajo) -- arranca apagado, el usuario lo prende a mano o lo
      // confirma vía el diálogo de candidatos detectados automáticamente.
      mergedTransactions.push({ ...tx, id: nextId++, _jobIndex: jobIdx, es_recurrente: false });
    });
  });
  return { mergedTransactions, accounts };
};
// 4 ago 2026: importador CSV/Excel sin IA (endpoints/bulk_import.py) --
// diferenciador gratis del plan Personal (Augusto: "qué otro tipo de
// colección de ingresos o egresos podemos dar a un cliente regular que no
// cueste"). A diferencia de createStatementJob, esto NO crea un job en
// background -- el parseo es instantáneo (sin IA de por medio), así que
// se resuelve en la misma llamada. Devuelve el mismo shape
// {account, transactions} que el resultado de un job de PDF, para poder
// reutilizar exactamente la misma pantalla de revisión de más abajo.
const importCsvFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post('/imports/csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data; // { account: null, transactions: [...], errores }
};
// Plantilla CSV descargable (ver botón "Descargar plantilla" en el paso 0)
// -- los encabezados en español calzan con HEADER_ALIASES en
// bulk_import.py, y "tipo" acepta "ingreso"/"gasto" (ver _build_raw_item).
// Se genera en el navegador con un Blob -- no hace falta pedirle este
// archivo al backend.
//
// 30 ago 2026 (Augusto: "la plantilla para subir archivos CSV no tiene
// moneda" -- un archivo con gastos en dólares no tenía forma de marcarlo
// sin inventar una columna aparte): se agrega "moneda" como columna
// opcional -- PEN (o vacío, que es lo mismo) para soles, USD para
// dólares. Ver el alias "currency" en bulk_import.py::HEADER_ALIASES.
const CSV_TEMPLATE_CONTENT =
  'fecha,descripcion,monto,moneda,tipo,categoria,proveedor\n' +
  '2026-08-01,Compra en Wong,150.50,PEN,gasto,alimentacion,Wong\n' +
  '2026-08-05,Pago de sueldo,3000,PEN,ingreso,salario,\n' +
  '2026-08-10,Suscripcion Netflix,15.99,USD,gasto,entretenimiento,Netflix\n' +
  '2026-08-10,Uber al aeropuerto,45,PEN,gasto,transporte,Uber\n';
const downloadCsvTemplate = () => {
  const blob = new Blob([CSV_TEMPLATE_CONTENT], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'plantilla_movimientos_pcm.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
const JOB_POLL_INTERVAL_MS = 2500;
// 5 ago 2026: cuántas consultas seguidas puede fallar (red, 502/503,
// timeout) antes de rendirse y mostrar "no se pudo consultar el estado
// del archivo". Antes cualquier falla puntual mataba el polling entero --
// eso pasó de verdad mientras Augusto probaba en vivo justo cuando se
// desplegaba un cambio nuevo: Render tarda unos segundos en reiniciar la
// instancia y durante ese hueco la consulta puede fallar aunque el job
// siga corriendo bien en el servidor. Con 2.5s entre intentos, 6 fallas
// seguidas tolera ~15s de caída -- de sobra para un reinicio de deploy --
// sin ocultar una falla real y persistente.
const MAX_POLL_FAILURES = 6;
const pollJobUntilDone = async (jobId) => {
  let consecutiveFailures = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const { data } = await apiClient.get(`/statements/jobs/${jobId}`);
      consecutiveFailures = 0;
      if (data.status === 'done' || data.status === 'error') {
        return data; // { id, filename, status, result: {account, transactions} | null, error }
      }
    } catch (err) {
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_POLL_FAILURES) {
        throw err;
      }
    }
    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
  }
};
// Función para obtener el icono de categoría
const getCategoryIcon = (category) => {
  switch (category) {
    case 'food': return <FastfoodIcon />;
    case 'transport': return <DirectionsCarIcon />;
    case 'bills': return <HomeIcon />;
    case 'shopping': return <ShoppingCartIcon />;
    case 'entertainment': return <TheaterComedyIcon />;
    default: return <MoreHorizIcon />;
  }
};
// 5 ago 2026 (Augusto: "por qué no ofreces más colores para nuevas
// categorías y aparece todo en gris"): antes CUALQUIER categoría fuera de
// las 5 con color fijo de abajo caía en el "default" gris -- incluía
// "salary"/"other" (que ya tenían un color implícito perdido) y sobre
// todo cualquier categoría que el cliente creara (ver
// endpoints/categories.py, que no guarda color). En vez de pedirle al
// cliente que elija un color a mano (más pasos, más fricción), se asigna
// uno determinístico de esta paleta según el nombre de la categoría --
// mismo nombre siempre cae en el mismo color, y hay variedad real en vez
// de gris uniforme.
const CATEGORY_COLOR_PALETTE = [
  '#1E88E5', '#43A047', '#8E24AA', '#FB8C00', '#00897B',
  '#3949AB', '#D81B60', '#6D4C41', '#00ACC1', '#7CB342',
  '#F4511E', '#5E35B1',
];
const hashCategoryToColor = (name) => {
  const str = String(name || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return CATEGORY_COLOR_PALETTE[hash % CATEGORY_COLOR_PALETTE.length];
};
// Función para obtener el color de categoría
const getCategoryColor = (category) => {
  switch (category) {
    case 'food': return '#1E88E5';
    case 'transport': return '#43A047';
    case 'bills': return '#E53935';
    case 'shopping': return '#8E24AA';
    case 'entertainment': return '#FB8C00';
    case 'salary': return '#2E7D32';
    case 'other': return '#757575';
    default: return hashCategoryToColor(category);
  }
};
// Obtener la lista de categorías con idioma
const getCategories = (language) => {
  const isSpanish = language === 'es';
  return [
    { value: 'food', label: isSpanish ? 'Alimentación' : 'Food & Dining' },
    { value: 'transport', label: isSpanish ? 'Transporte' : 'Transport' },
    { value: 'bills', label: isSpanish ? 'Facturas & Servicios' : 'Bills & Utilities' },
    { value: 'shopping', label: isSpanish ? 'Compras' : 'Shopping' },
    { value: 'entertainment', label: isSpanish ? 'Entretenimiento' : 'Entertainment' },
    { value: 'salary', label: isSpanish ? 'Salario' : 'Salary' },
    { value: 'other', label: isSpanish ? 'Otros' : 'Other' }
  ];
};
// Componente principal
const StatementImporter = ({ onClose }) => {
  const theme = useTheme();
  const { language, t } = useLanguage();
  const isSpanish = language === 'es';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // 5 ago 2026: historial de transacciones + recurrentes ya activos, para
  // el detector de candidatos a recurrente (ver handleClickImportar más
  // abajo) -- este diálogo vive en el header, montado aparte de
  // TransactionsPage, así que no puede asumir que ya estén cargados en
  // Redux; se piden de nuevo acá mismo (fetchTransactions/fetchIncomes/
  // fetchExpenses son idempotentes, no duplican nada si ya estaban).
  const transactionsHistorial = useSelector((state) => state.transactions.list);
  const recurringIncomes = useSelector((state) => state.recurring.incomes);
  const recurringExpenses = useSelector((state) => state.recurring.expenses);
  const fileInputRef = useRef(null);
  // 5 ago 2026: mismo mecanismo de NotionSidebar.js para decidir si se
  // muestra el botón "Ignorar y continuar" -- el control de acceso real
  // vive en el backend (ver admin_override en statements.py); esto es
  // solo para no confundir al resto de clientes con un botón que igual
  // el backend les va a rechazar.
  // 17 sep 2026: la lista de emails vive ahora en utils/roles.js.
  const { user } = useAuth();
  const isAdminUser = esAdmin(user);
  // 5 ago 2026 (Augusto: "no veo la opción de Ignorar para mi cuenta"):
  // antes "Ignorar y continuar" solo aparecía DESPUÉS de subir un archivo
  // y que rebotara con 402 (ver job.quotaExceeded más abajo) -- para un
  // admin con 0 páginas, eso significaba subir a ciegas un archivo que ya
  // se sabía que iba a fallar, solo para desbloquear el botón. Este
  // toggle vive en el aviso de arriba (antes de elegir archivo) y, si el
  // admin lo activa, createStatementJob ya manda admin_override=true
  // desde el primer intento -- ver handleProcessFiles. Se deja también
  // handleAdminIgnoreQuota (reintento por archivo) para el caso en que no
  // lo haya activado antes y el archivo rebote igual.
  const [adminBypassQuota, setAdminBypassQuota] = useState(false);
  // Estados
  const [activeStep, setActiveStep] = useState(0);
  // 3 ago 2026 (pedido de Augusto): antes solo se podía subir un PDF a la
  // vez (selectedFile, singular) — ahora es una lista, y cada archivo se
  // procesa como su propio job en el backend (ver jobs más abajo).
  const [selectedFiles, setSelectedFiles] = useState([]);
  // 25 sep 2026: páginas contadas en el navegador por archivo elegido, para
  // el aviso previo al proceso (ver contarPaginasPdf arriba). Clave:
  // "nombre-tamaño", el mismo criterio con el que addFiles evita repetidos.
  // Valor: número de páginas, o null si el PDF no dejó contarlas.
  const [paginasPorArchivo, setPaginasPorArchivo] = useState({});
  const [parsedTransactions, setParsedTransactions] = useState([]);
  // Una cuenta/tarjeta detectada por archivo (antes era un solo objeto,
  // detectedAccount, porque solo se podía subir un PDF). Cada entrada trae
  // _jobIndex para poder volver a agrupar sus transacciones al importar
  // (ver handleImportTransactions).
  const [detectedAccounts, setDetectedAccounts] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  // 5 ago 2026: diálogo de "¿alguno de estos es recurrente?" antes de
  // confirmar el import (ver handleClickImportar más abajo).
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [recurringCandidatos, setRecurringCandidatos] = useState([]);
  // 17 sep 2026 (pedido de Augusto: "pagos a tarjeta de crédito u otros
  // préstamos tienen que ser considerados como transferencias de una
  // cuenta ahorros o cuenta corriente u otra deuda"): un pago detectado
  // en el estado de cuenta de una tarjeta (tipo "income") no trae de qué
  // cuenta salió el dinero -- el propio PDF de la tarjeta no lo sabe. Se
  // le pregunta a Augusto antes de importar, con este diálogo, y se
  // manda su elección como cuenta_origen_id (ver handleImportTransactions
  // y backend_new/.../statements.py) para que se cree como Transferencia
  // real en vez de una Transaction "ingreso" suelta.
  const [misCuentas, setMisCuentas] = useState([]);
  const [pagoOrigenDialogOpen, setPagoOrigenDialogOpen] = useState(false);
  const [pagosPendientesOrigen, setPagosPendientesOrigen] = useState([]); // filas que necesitan cuenta de origen
  const [pagoOrigenSeleccion, setPagoOrigenSeleccion] = useState({}); // { [rowId]: cuentaOrigenId }
  useEffect(() => {
    apiClient.get('/dashboard/accounts').then((res) => setMisCuentas(res.data || [])).catch(() => setMisCuentas([]));
  }, []);
  // 21 sep 2026 (Augusto: "debería permitir añadir una nueva al
  // seleccionar la fuente de donde proviene el dinero, sea deuda u otra
  // cuenta"). Antes esto filtraba solo ahorros/corriente, y eso dejaba
  // afuera un caso real: pagar una tarjeta con otra tarjeta, o con una
  // línea de crédito. El backend nunca tuvo ese límite -- transferir
  // desde una tarjeta está explícitamente permitido en
  // crear_transferencia (no hay "saldo disponible" que validar ahí).
  // Era una restricción que solo existía en esta pantalla.
  //
  // Se ordenan las cuentas de activo primero porque siguen siendo el caso
  // habitual, pero las deudas quedan disponibles abajo.
  const TIPOS_ACTIVO = ['ahorros', 'corriente', 'efectivo'];
  const cuentasOrigenElegibles = useMemo(() => {
    const activos = misCuentas.filter((c) => TIPOS_ACTIVO.includes(c.tipo));
    const deudas = misCuentas.filter((c) => !TIPOS_ACTIVO.includes(c.tipo));
    return [...activos, ...deudas];
  }, [misCuentas]);
  const esCuentaDeActivo = (c) => TIPOS_ACTIVO.includes(c?.tipo);

  // Alta rápida de cuenta desde el propio selector de origen -- mismo
  // patrón que TransactionsPage.js. Sin esto, alguien que paga su tarjeta
  // desde una cuenta que todavía no registró en PCM queda trabado: no
  // puede elegirla, no puede crearla sin salir, y salir del importador
  // significa perder el archivo ya procesado.
  const [openNuevaCuentaOrigen, setOpenNuevaCuentaOrigen] = useState(false);
  const [filaParaNuevaCuenta, setFilaParaNuevaCuenta] = useState(null);
  const [nuevaCuentaOrigen, setNuevaCuentaOrigen] = useState({
    nombre: '', tipo: 'ahorros', moneda: 'PEN', balance: '',
  });
  const [guardandoCuentaOrigen, setGuardandoCuentaOrigen] = useState(false);
  const [errorCuentaOrigen, setErrorCuentaOrigen] = useState(null);

  const abrirNuevaCuentaOrigen = (rowId) => {
    setFilaParaNuevaCuenta(rowId);
    setNuevaCuentaOrigen({ nombre: '', tipo: 'ahorros', moneda: 'PEN', balance: '' });
    setErrorCuentaOrigen(null);
    setOpenNuevaCuentaOrigen(true);
  };

  const handleGuardarCuentaOrigen = async () => {
    if (!nuevaCuentaOrigen.nombre.trim()) return;
    setGuardandoCuentaOrigen(true);
    setErrorCuentaOrigen(null);
    try {
      const { data } = await apiClient.post('/dashboard/accounts', {
        nombre: nuevaCuentaOrigen.nombre.trim(),
        tipo: nuevaCuentaOrigen.tipo,
        moneda: nuevaCuentaOrigen.moneda,
        saldo: parseFloat(nuevaCuentaOrigen.balance) || 0,
      });
      setMisCuentas((prev) => [...prev, data]);
      // Se selecciona sola en la fila desde donde se pidió crearla: quien
      // la acaba de crear es porque la quiere usar justo ahí.
      if (filaParaNuevaCuenta != null) {
        setPagoOrigenSeleccion((prev) => ({ ...prev, [filaParaNuevaCuenta]: data.id }));
      }
      setOpenNuevaCuentaOrigen(false);
    } catch (err) {
      setErrorCuentaOrigen(
        err.response?.data?.detail || (isSpanish ? 'No se pudo crear la cuenta.' : 'Could not create the account.')
      );
    } finally {
      setGuardandoCuentaOrigen(false);
    }
  };
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  // Categorías propias del usuario (endpoints/categories.py), además de las
  // 7 categorías base fijas — antes cualquier movimiento que no calzara en
  // esas 7 quedaba forzado a "Otros"; ahora se pueden crear las que hagan
  // falta desde el mismo diálogo de edición.
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  // 5 ago 2026: aviso breve de "esto también se aplicó a otras N filas" --
  // ver propagateToMatchingRows más abajo.
  const [autoAppliedNotice, setAutoAppliedNotice] = useState(null);
  // Estado de cada job de procesamiento en curso (uno por archivo
  // seleccionado) — reemplaza a la barra de progreso de bytes de antes,
  // que ya no aplica: ahora la subida es rápida (solo crea el job) y el
  // trabajo real pasa en background del lado del servidor, sin una
  // conexión larga que medir. 'processing' cubre tanto "pending" como
  // "processing" del backend — la distinción no le importa al usuario.
  const [jobs, setJobs] = useState([]);
  const [processingMessage, setProcessingMessage] = useState('');
  // 4 ago 2026: 'pdf' = importador con IA (existente, sujeto a cuota de
  // páginas y a plan Pro+), 'csv' = importador de CSV/Excel sin IA (nuevo,
  // gratis, disponible desde el plan Free -- ver bulk_import.py). Ambos
  // modos comparten el mismo paso de revisión (activeStep === 1) y el
  // mismo endpoint de confirmación (POST /statements/import) más abajo --
  // solo cambia cómo se llega a parsedTransactions en el paso 0.
  const [importMode, setImportMode] = useState('pdf');
  // 4 ago 2026 (cuota de páginas por plan, ver backend_new/app/core/plans.py):
  // quota = respuesta de GET /statements/quota (null mientras carga o si
  // falló). pagePacks = catálogo de paquetes de páginas extra (GET
  // /subscription/page-packs). buyDialogOpen abre el diálogo para comprar
  // uno; buyingPackId marca qué botón está en curso de redirigir a pagar
  // (formato "packId-proveedor", igual que Subscription.js).
  const [quota, setQuota] = useState(null);
  const [pagePacks, setPagePacks] = useState([]);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [buyingPackId, setBuyingPackId] = useState(null);
  const refreshQuota = () => {
    apiClient.get('/statements/quota')
      .then(res => setQuota(res.data))
      .catch(() => {
        // No es crítico -- si falla, simplemente no se muestra el aviso de
        // cuota. El backend igual valida al subir cada archivo.
      });
  };
  useEffect(() => {
    categoryService.getAll()
      .then(res => setCustomCategories(res.data || []))
      .catch(() => {
        // Si falla (ej. sesión vencida), simplemente no se muestran
        // categorías propias — las 7 base siguen disponibles igual.
      });
    refreshQuota();
    apiClient.get('/subscription/page-packs')
      .then(res => setPagePacks(res.data || []))
      .catch(() => {
        // Sin esto solo se pierde la opción de comprar páginas extra desde
        // acá -- no es bloqueante para subir/procesar archivos.
      });
    // 5 ago 2026: datos para el detector de candidatos a recurrente (ver
    // transactionsHistorial/recurringIncomes/recurringExpenses arriba).
    dispatch(fetchTransactions());
    dispatch(fetchIncomes());
    dispatch(fetchExpenses());
  }, [dispatch]);
  // 25 sep 2026: cuenta las páginas de cada PDF recién elegido, de a uno.
  // Secuencial a propósito: contar implica leer el archivo entero en
  // memoria, y seis archivos de 8 MB en paralelo es pedirle al navegador
  // algo que no necesita hacer. `vivo` evita escribir estado si el
  // componente se cerró en el medio.
  useEffect(() => {
    if (importMode !== 'pdf') return undefined;
    const pendientes = selectedFiles.filter(
      (f) => !(`${f.name}-${f.size}` in paginasPorArchivo)
    );
    if (pendientes.length === 0) return undefined;
    let vivo = true;
    (async () => {
      for (const file of pendientes) {
        // Cada setState de abajo vuelve a disparar este efecto, que arranca
        // su propio recorrido con los que faltan; sin este corte el ciclo
        // viejo seguiría leyendo archivos que el nuevo ya está leyendo.
        if (!vivo) return;
        const paginas = await contarPaginasPdf(file);
        if (!vivo) return;
        setPaginasPorArchivo((prev) => ({ ...prev, [`${file.name}-${file.size}`]: paginas }));
      }
    })();
    return () => {
      vivo = false;
    };
  }, [selectedFiles, paginasPorArchivo, importMode]);
  const handleBuyPack = async (packId, provider) => {
    setBuyingPackId(`${packId}-${provider}`);
    try {
      const { data } = await apiClient.post(`/payments/${provider}/checkout-pages?pack_id=${packId}`);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(isSpanish ? 'No se recibió el link de pago.' : 'No payment link received.');
        setBuyingPackId(null);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          (isSpanish ? `No se pudo iniciar el pago con ${provider}.` : `Could not start payment with ${provider}.`)
      );
      setBuyingPackId(null);
    }
  };
  // 3 ago 2026 (pedido de Augusto): el monto mostraba siempre "$" sin
  // importar la moneda real del estado de cuenta -- un estado en soles
  // (PEN) se veía con símbolo de dólar. La moneda es un dato de la CUENTA
  // detectada (account.moneda, ver EXTRACTION_SYSTEM_PROMPT en el
  // backend), no de cada transacción individual, así que hay que buscarla
  // por _jobIndex (el archivo del que vino esa fila) en detectedAccounts.
  // 5 ago 2026 (Augusto: un cargo de $723 en dólares se mostraba como si
  // fueran soles -- ver moneda_override en endpoints/statements.py): si
  // ESTE movimiento puntual viene con una moneda distinta a la de la
  // cuenta (típico en tarjetas peruanas con columnas separadas de Soles y
  // Dólares por fila), se usa esa en vez de la moneda general de la
  // cuenta.
  const getRowCurrencySymbol = (row) => {
    if (row?.moneda_override) return getCurrencySymbol(row.moneda_override);
    const account = detectedAccounts.find((a) => a._jobIndex === row?._jobIndex);
    return getCurrencySymbol(account?.moneda || 'USD');
  };
  // Texto legible para el tipo de cuenta/tarjeta detectado.
  const getAccountKindLabel = (tipoCuenta) => {
    const map = {
      tarjeta_credito: isSpanish ? 'Tarjeta de crédito' : 'Credit card',
      ahorros: isSpanish ? 'Cuenta de ahorros' : 'Savings account',
      corriente: isSpanish ? 'Cuenta corriente' : 'Checking account',
      otro: isSpanish ? 'Cuenta' : 'Account',
    };
    return map[tipoCuenta] || (isSpanish ? 'Cuenta' : 'Account');
  };

  // Lista de categorías: las 7 base (con ícono/color propio) + las que el
  // usuario haya creado. Se deduplica por nombre en minúsculas por si una
  // categoría propia coincide con una base (ej. el usuario ya tenía
  // "Salario" creada).
  // Mensajes que rotan durante la fase de IA (sin porcentaje real posible,
  // ver comentario junto a processingPhase). Se muestran uno a la vez cada
  // pocos segundos solo para que la espera no se sienta trabada, sobre
  // todo en PDFs escaneados que tardan más.
  const aiProcessingMessages = useMemo(() => (
    isSpanish
      ? [
          'Leyendo el PDF...',
          'Extrayendo movimientos con IA...',
          'Identificando categorías y proveedores...',
          'Casi listo — los PDFs escaneados pueden tardar más...',
        ]
      : [
          'Reading the PDF...',
          'Extracting transactions with AI...',
          'Identifying categories and merchants...',
          'Almost there — scanned PDFs can take longer...',
        ]
  ), [isSpanish]);
  const categories = getCategories(language);
  // 25 sep 2026 (Augusto: "las categorías deben de estar ordenadas
  // alfabéticamente cuando se necesite cambiar categorías al momento de
  // importar transacciones").
  //
  // Salían en el orden en que están escritas en el código, y las que
  // agrega el usuario iban todas al final, después de "Otros". Revisando
  // ochenta movimientos de un extracto uno abre ese desplegable decenas
  // de veces: sin orden, cada vez hay que leer la lista entera.
  //
  // "Otros" se queda al final a propósito: no es una categoría más, es
  // el descarte. Ordenarlo entre la A y la Z lo pondría a competir con
  // opciones reales.
  const allCategories = useMemo(() => {
    const extra = customCategories
      .map(c => ({ value: c.nombre.toLowerCase(), label: c.nombre }))
      .filter(c => !categories.some(base => base.value === c.value));
    const todas = [...categories, ...extra];
    const descarte = todas.filter((c) => c.value === 'other');
    const resto = todas
      .filter((c) => c.value !== 'other')
      // localeCompare para que Ñ y los acentos queden donde un
      // hispanohablante los busca, no donde caen por código de carácter.
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    return [...resto, ...descarte];
  }, [categories, customCategories]);
  // 25 sep 2026: resumen de lo que el usuario acaba de elegir, para
  // decidirlo ANTES de empezar y no a mitad de camino (ver contarPaginasPdf
  // arriba). Tres cosas que antes no se decían en ninguna parte: cuántas
  // páginas de la cuota se van, que los archivos se procesan de a uno (el
  // backend serializa los trabajos, _LIMITE_TRABAJOS en statements.py), y si
  // la cuota no alcanza para todo el lote.
  const resumenSeleccion = useMemo(() => {
    if (importMode !== 'pdf' || selectedFiles.length === 0) return null;
    let paginas = 0;
    let sinContar = 0;
    selectedFiles.forEach((f) => {
      const valor = paginasPorArchivo[`${f.name}-${f.size}`];
      if (valor === undefined) sinContar += 1; // todavía contando
      else if (valor === null) sinContar += 1; // el PDF no dejó contarlas
      else paginas += valor;
    });
    const disponibles = quota && !quota.unlimited ? quota.pages_available_total : null;
    return {
      archivos: selectedFiles.length,
      paginas,
      sinContar,
      // Solo se avisa que no alcanza cuando se pudieron contar TODAS las
      // páginas: con un conteo parcial el aviso sería adivinanza.
      noAlcanza: disponibles !== null && sinContar === 0 && paginas > disponibles,
      disponibles,
    };
  }, [selectedFiles, paginasPorArchivo, importMode, quota]);
  // Pasos del proceso
  const steps = [
    {
      label: isSpanish ? 'Subir archivo' : 'Upload File',
      description: isSpanish ? 'Sube tu estado de cuenta bancario' : 'Upload your bank statement'
    },
    {
      label: isSpanish ? 'Revisar transacciones' : 'Review Transactions',
      description: isSpanish ? 'Verifica y categoriza las transacciones extraídas' : 'Verify and categorize extracted transactions'
    },
    {
      label: isSpanish ? 'Importar' : 'Import',
      description: isSpanish ? 'Importa las transacciones al sistema' : 'Import transactions to the system'
    }
  ];

  // Manejadores de eventos
  const addFiles = (fileList) => {
    // 4 ago 2026: qué extensión se acepta depende del modo elegido arriba
    // (importMode) -- PDF para el importador con IA, CSV/XLSX para el
    // importador gratis sin IA.
    const incoming = Array.from(fileList || []).filter((f) => {
      const name = f.name.toLowerCase();
      if (importMode === 'csv') {
        return name.endsWith('.csv') || name.endsWith('.xlsx');
      }
      return f.type === 'application/pdf' || name.endsWith('.pdf');
    });
    if (incoming.length === 0) return;
    setSelectedFiles((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const merged = [...prev];
      incoming.forEach((f) => {
        const key = `${f.name}-${f.size}`;
        if (!existingKeys.has(key)) {
          merged.push(f);
          existingKeys.add(key);
        }
      });
      return merged;
    });
  };
  const handleFileSelect = (event) => {
    addFiles(event.target.files);
    // Sin esto, elegir de nuevo el mismo archivo después de quitarlo de la
    // lista no dispara onChange (el input ve el mismo value de antes).
    event.target.value = '';
  };
  const handleDragOver = (event) => {
    event.preventDefault();
  };
  const handleDrop = (event) => {
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  };
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };
  // 4 ago 2026: procesa archivos CSV/Excel (sin IA, ver importCsvFile
  // arriba) -- mucho más simple que el flujo de PDF: no hay job en
  // background que consultar, cada archivo se resuelve en su propia
  // llamada HTTP. Al final deja los mismos parsedTransactions/
  // detectedAccounts que el flujo de PDF, así que el paso 1 (revisión) y
  // el paso 2 (importar) de más abajo no necesitan saber de qué modo
  // vinieron los datos.
  const handleProcessCsvFiles = async () => {
    if (selectedFiles.length === 0) return;
    setLoading(true);
    setError(null);
    setProcessingMessage(isSpanish ? 'Leyendo el archivo...' : 'Reading the file...');
    const initialJobs = selectedFiles.map((f) => ({
      id: null,
      fileName: f.name,
      status: 'processing',
      error: null,
      transactionsCount: null,
    }));
    setJobs(initialJobs);
    try {
      const results = await Promise.all(
        selectedFiles.map(async (file, idx) => {
          try {
            const data = await importCsvFile(file);
            const updated = {
              id: `csv-${idx}`,
              fileName: file.name,
              status: 'done',
              error:
                data.errores > 0
                  ? isSpanish
                    ? `${data.errores} fila(s) no se pudieron reconocer.`
                    : `${data.errores} row(s) could not be recognized.`
                  : null,
              transactionsCount: data.transactions?.length ?? 0,
              account: data.account || null,
              transactions: data.transactions || [],
            };
            setJobs((prev) => prev.map((j, i) => (i === idx ? updated : j)));
            return updated;
          } catch (err) {
            const detail = err.response?.data?.detail;
            const updated = {
              id: null,
              fileName: file.name,
              status: 'error',
              error: detail || (isSpanish ? 'No se pudo leer el archivo.' : 'Could not read the file.'),
              transactionsCount: null,
            };
            setJobs((prev) => prev.map((j, i) => (i === idx ? updated : j)));
            return updated;
          }
        })
      );
      let nextId = 1;
      const mergedTransactions = [];
      const accounts = [];
      results.forEach((job, jobIdx) => {
        if (job.status !== 'done') return;
        if (job.account) accounts.push({ ...job.account, _fileName: job.fileName, _jobIndex: jobIdx, _avisos: job.avisos || [] });
        (job.transactions || []).forEach((tx) => {
          mergedTransactions.push({ ...tx, id: nextId++, _jobIndex: jobIdx, es_recurrente: false });
        });
      });
      setParsedTransactions(mergedTransactions);
      // 28 ago 2026 (pedido de Augusto): arranca todo seleccionado EXCEPTO
      // lo marcado como posible_duplicado -- así el checkbox significa lo
      // que parece que significa (marcado = se importa) y un duplicado
      // real requiere un clic deliberado para incluirlo, en vez de que
      // dejar todo destildado lo importe igual.
      // 21 sep 2026: ahora arrancan TODAS seleccionadas, duplicados
      // incluidos. Entran igual, marcadas, y se deciden adentro de PCM
      // (ver revision_motivo en el backend). Dejarlas fuera por defecto
      // era lo que hacía que 3 de 81 desaparecieran sin que nadie se
      // enterara.
      setSelectedRows(mergedTransactions.map((t) => t.id));
      setDetectedAccounts(accounts);
      const failed = results.filter((j) => j.status === 'error');
      if (mergedTransactions.length > 0) {
        if (failed.length > 0) {
          setError(
            isSpanish
              ? `${failed.length} de ${results.length} archivo(s) no se pudieron procesar: ${failed
                  .map((f) => `${f.fileName} (${f.error})`)
                  .join('; ')}`
              : `${failed.length} of ${results.length} file(s) could not be processed: ${failed
                  .map((f) => `${f.fileName} (${f.error})`)
                  .join('; ')}`
          );
        }
        setActiveStep(1);
      } else {
        setError(
          failed.map((f) => `${f.fileName}: ${f.error}`).join(' — ') ||
            (isSpanish ? 'No se pudo procesar ningún archivo.' : 'Could not process any file.')
        );
      }
    } finally {
      setLoading(false);
    }
  };
  const handleProcessFiles = async () => {
    // 4 ago 2026: el modo CSV/Excel (sin IA) tiene su propio flujo, mucho
    // más simple (sin jobs en background) -- ver handleProcessCsvFiles.
    if (importMode === 'csv') {
      return handleProcessCsvFiles();
    }
    if (selectedFiles.length === 0) return;
    setLoading(true);
    setError(null);
    // Rota un mensaje genérico de "sigue trabajando" mientras dure el
    // procesamiento — ya no hay un porcentaje real que mostrar (la subida
    // en sí es rápida; lo que tarda es la IA, en background del lado del
    // servidor, ver createStatementJob/pollJobUntilDone más arriba).
    let messageIndex = 0;
    setProcessingMessage(aiProcessingMessages[0]);
    const rotateInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % aiProcessingMessages.length;
      setProcessingMessage(aiProcessingMessages[messageIndex]);
    }, 4000);
    try {
      // 1) Crear un job por archivo — llamadas cortas, solo suben el
      //    archivo y devuelven un id (el trabajo real pasa después, en
      //    background del lado del servidor).
      const initialJobs = [];
      for (const file of selectedFiles) {
        try {
          // 5 ago 2026: si el admin activó "Ignorar y continuar" en el
          // aviso de cuota (ver adminBypassQuota más arriba), se manda
          // admin_override=true desde este primer intento -- el backend
          // igual revalida el email antes de saltarse la cuota.
          const created = await createStatementJob(file, adminBypassQuota);
          initialJobs.push({
            id: created.id,
            fileName: file.name,
            status: 'processing',
            error: null,
            transactionsCount: null,
          });
        } catch (err) {
          const detail = err.response?.data?.detail;
          // 4 ago 2026: 402 = se acabó la cuota de páginas del mes (ver
          // _check_page_quota en el backend) -- el detail que manda el
          // backend ya trae los números exactos (páginas del archivo,
          // cuántas quedan, cuántas faltan), así que se muestra tal cual y
          // además se ofrece el botón de comprar un paquete extra (ver
          // quotaExceeded más abajo, en el render de la lista de jobs).
          initialJobs.push({
            id: null,
            fileName: file.name,
            status: 'error',
            error: detail || (isSpanish ? 'No se pudo subir el archivo.' : 'Could not upload the file.'),
            transactionsCount: null,
            quotaExceeded: err.response?.status === 402,
          });
        }
      }
      setJobs(initialJobs);
      // 2) Consultar cada job (en paralelo, uno por archivo) hasta que
      //    termine — así varios estados de cuenta se procesan a la vez en
      //    vez de uno detrás de otro.
      const finalResults = await Promise.all(
        initialJobs.map(async (job, idx) => {
          if (!job.id) return job; // ya falló al subir, nada que consultar
          try {
            const result = await pollJobUntilDone(job.id);
            const updated = {
              ...job,
              status: result.status,
              error: result.error,
              transactionsCount: result.result?.transactions?.length ?? 0,
              account: result.result?.account || null,
              transactions: result.result?.transactions || [],
              // 30 oct 2026: controles de lectura del backend (avisos_de_lectura).
              avisos: result.result?.avisos || [],
            };
            setJobs((prev) => prev.map((j, i) => (i === idx ? updated : j)));
            return updated;
          } catch (err) {
            const updated = {
              ...job,
              status: 'error',
              error: isSpanish ? 'No se pudo consultar el estado del archivo.' : 'Could not check file status.',
            };
            setJobs((prev) => prev.map((j, i) => (i === idx ? updated : j)));
            return updated;
          }
        })
      );
      // 3) Fusionar las transacciones de todos los archivos que sí
      //    terminaron bien en una sola lista para revisar juntas — cada
      //    transacción guarda _jobIndex para poder volver a agrupar por
      //    cuenta de origen al importar (ver handleImportTransactions).
      const { mergedTransactions, accounts } = mergeJobsForReview(finalResults);
      setParsedTransactions(mergedTransactions);
      // Ver comentario arriba (21 sep 2026): entran todas, las marcadas
      // se deciden después desde Revisión pendiente.
      setSelectedRows(mergedTransactions.map((t) => t.id));
      setDetectedAccounts(accounts);
      const failed = finalResults.filter((j) => j.status === 'error');
      if (mergedTransactions.length > 0) {
        if (failed.length > 0) {
          // Éxito parcial: se avanza igual con lo que sí se pudo leer,
          // pero se avisa cuáles archivos fallaron y por qué.
          setError(
            isSpanish
              ? `${failed.length} de ${finalResults.length} archivo(s) no se pudieron procesar: ${failed
                  .map((f) => `${f.fileName} (${f.error})`)
                  .join('; ')}`
              : `${failed.length} of ${finalResults.length} file(s) could not be processed: ${failed
                  .map((f) => `${f.fileName} (${f.error})`)
                  .join('; ')}`
          );
        }
        setActiveStep(1);
      } else {
        setError(
          failed.map((f) => `${f.fileName}: ${f.error}`).join(' — ') ||
            (isSpanish ? 'No se pudo procesar ningún archivo.' : 'Could not process any file.')
        );
      }
    } finally {
      clearInterval(rotateInterval);
      setLoading(false);
      // Los archivos que sí se procesaron ya consumieron páginas del mes
      // (ver _log_ai_usage en el backend) -- se refresca acá para que el
      // aviso de cuota (arriba del paso 1) quede al día.
      refreshQuota();
    }
  };
  // 5 ago 2026 (pedido de Augusto: "me debe de salir el mensaje que le
  // sale al cliente, pero luego debo de tener yo, solamente (admin
  // accounts), la opción de ignorar y continuar"): reintenta un job
  // puntual que quedó en quotaExceeded, esta vez con admin_override=true.
  // El botón que llama a esto solo se muestra si isAdminUser (ver
  // renderStepContent) -- aun así el backend vuelve a validar el email
  // antes de saltarse la cuota, así que no depende de esta capa.
  const handleAdminIgnoreQuota = async (jobIdx) => {
    const job = jobs[jobIdx];
    if (!job) return;
    const file = selectedFiles.find((f) => f.name === job.fileName);
    if (!file) return;
    setJobs((prev) =>
      prev.map((j, i) => (i === jobIdx ? { ...j, status: 'processing', error: null, quotaExceeded: false } : j))
    );
    let updatedJob;
    try {
      const created = await createStatementJob(file, true);
      try {
        const result = await pollJobUntilDone(created.id);
        updatedJob = {
          id: created.id,
          fileName: file.name,
          status: result.status,
          error: result.error,
          transactionsCount: result.result?.transactions?.length ?? 0,
          account: result.result?.account || null,
          transactions: result.result?.transactions || [],
          avisos: result.result?.avisos || [],
        };
      } catch (pollErr) {
        updatedJob = {
          id: created.id,
          fileName: file.name,
          status: 'error',
          error: isSpanish ? 'No se pudo consultar el estado del archivo.' : 'Could not check file status.',
        };
      }
    } catch (err) {
      updatedJob = {
        id: null,
        fileName: file.name,
        status: 'error',
        error: err.response?.data?.detail || (isSpanish ? 'No se pudo subir el archivo.' : 'Could not upload the file.'),
      };
    }
    const nextJobs = jobs.map((j, i) => (i === jobIdx ? updatedJob : j));
    setJobs(nextJobs);
    refreshQuota();
    const { mergedTransactions, accounts } = mergeJobsForReview(nextJobs);
    if (mergedTransactions.length > 0) {
      setParsedTransactions(mergedTransactions);
      // Ver comentario arriba (21 sep 2026): entran todas, las marcadas
      // se deciden después desde Revisión pendiente.
      setSelectedRows(mergedTransactions.map((t) => t.id));
      setDetectedAccounts(accounts);
      setActiveStep(1);
    }
  };
  const handleEditTransaction = (transaction) => {
    setCurrentTransaction(transaction);
    setEditDialogOpen(true);
  };
  // 5 ago 2026 (pedido de Augusto: "una vez categorizado un gasto, los
  // otros por los mismos conceptos y proveedores sean actualizados, y que
  // el usuario no tenga que hacerlo manualmente uno a uno"): cuando se
  // guarda la categoría/proveedor de UNA transacción del lote de revisión
  // (por el diálogo o edición inline en la tabla), se aplica el mismo
  // cambio a las demás filas del MISMO lote que compartan proveedor -- o,
  // si no hay proveedor detectado, que compartan la misma descripción
  // exacta. Esto es además de (no en vez de) la "memoria" de
  // merchant_rules.py, que ya recuerda la categoría de un proveedor para
  // la SIGUIENTE importación -- acá se resuelve el caso de HOY, dentro
  // del mismo estado de cuenta, donde "WU LAUS SAC" puede aparecer 5
  // veces y antes había que corregir cada una a mano.
  const propagateToMatchingRows = (transactions, updatedTx) => {
    const proveedorKey = (updatedTx.suggested_proveedor || '').trim().toLowerCase();
    const descriptionKey = (updatedTx.description || '').trim().toLowerCase();
    let matchCount = 0;
    const next = transactions.map((t) => {
      if (t.id === updatedTx.id) return updatedTx;
      const sameProveedor = proveedorKey && (t.suggested_proveedor || '').trim().toLowerCase() === proveedorKey;
      const sameDescription = !proveedorKey && descriptionKey && (t.description || '').trim().toLowerCase() === descriptionKey;
      if (sameProveedor || sameDescription) {
        matchCount += 1;
        return { ...t, suggested_category: updatedTx.suggested_category, suggested_proveedor: updatedTx.suggested_proveedor };
      }
      return t;
    });
    if (matchCount > 0) {
      setAutoAppliedNotice(
        isSpanish
          ? `Categoría aplicada también a ${matchCount} transacción${matchCount === 1 ? '' : 'es'} más de "${updatedTx.suggested_proveedor || updatedTx.description}".`
          : `Category also applied to ${matchCount} more transaction${matchCount === 1 ? '' : 's'} from "${updatedTx.suggested_proveedor || updatedTx.description}".`
      );
    }
    return next;
  };
  // 5 ago 2026: "no debe de haber la necesidad de presionar en Crear para
  // crear y asignar una nueva categoría, bastaría apretar Guardar" -- si
  // el cliente escribió algo en "Nueva categoría...", Guardar la crea (o
  // la reconoce si ya existía, ver create_category en categories.py, que
  // es idempotente) Y la asigna a la transacción actual en un solo paso,
  // en vez de exigir un clic aparte en "Crear" antes de poder guardar.
  const handleSaveEdit = async () => {
    let finalTransaction = currentTransaction;
    const pendingName = newCategoryName.trim();
    if (pendingName) {
      setSavingCategory(true);
      try {
        const response = await categoryService.create(pendingName);
        const created = response.data;
        setCustomCategories(prev => (prev.some(c => c.id === created.id) ? prev : [...prev, created]));
        finalTransaction = { ...currentTransaction, suggested_category: created.nombre.toLowerCase() };
      } catch (err) {
        // Si falla la creación, se guarda igual con la categoría que ya
        // estaba seleccionada -- no vale la pena bloquear todo el guardado
        // por esto.
      } finally {
        setSavingCategory(false);
      }
    }
    setNewCategoryName('');
    setParsedTransactions(prevTransactions => propagateToMatchingRows(prevTransactions, finalTransaction));
    setEditDialogOpen(false);
  };
  // 5 ago 2026: persiste un cambio de categoría/proveedor/monto/etc hecho
  // directamente en la celda de la tabla (ver columns más abajo,
  // editable: true) -- MUI DataGrid llama esto al confirmar la edición de
  // una celda. Reutiliza la misma propagación a filas iguales que el
  // diálogo completo.
  const handleProcessRowUpdate = (newRow) => {
    const cleaned = {
      ...newRow,
      amount: Math.round(Math.abs(Number(newRow.amount) || 0) * 100) / 100,
      description: (newRow.description || '').toString().trim() || 'Movimiento',
      suggested_proveedor: newRow.suggested_proveedor ? String(newRow.suggested_proveedor).trim() : null,
    };
    setParsedTransactions(prev => propagateToMatchingRows(prev, cleaned));
    return cleaned;
  };
  const handleCategoryChange = (category) => {
    setCurrentTransaction(prev => ({
      ...prev,
      suggested_category: category
    }));
  };
  const handleProveedorChange = (proveedor) => {
    setCurrentTransaction(prev => ({
      ...prev,
      suggested_proveedor: proveedor
    }));
  };

  // 5 ago 2026: antes de confirmar el import, si hay filas cuyo
  // proveedor+monto ya apareció hace ~1 mes (y no están marcadas
  // recurrente a mano todavía), se pregunta con un diálogo único -- ver
  // RecurringCandidatesDialog.js y utils/recurringDetection.js. Si no hay
  // candidatos, importa directo sin interrumpir el flujo normal.
  const handleClickImportar = () => {
    // 28 ago 2026 (pedido de Augusto: "por qué añade todas si yo no he
    // seleccionado ninguna, eso es un error, no?"): antes, dejar todo
    // destildado significaba "importar todo" -- justo lo opuesto de lo
    // que alguien esperaría de una casilla, y la razón de que reintentar
    // una importación duplicara transacciones aunque el usuario creyera
    // estar excluyéndolas. Ahora selectedRows SIEMPRE es la lista real a
    // importar (se precarga con todo menos los posibles_duplicado apenas
    // termina la extracción -- ver los 3 setParsedTransactions más
    // arriba), así que ya no hace falta (ni es correcto) caer de vuelta a
    // "todas" cuando queda vacía.
    const transactionsToImport = parsedTransactions.filter((t) => selectedRows.includes(t.id));
    // 17 sep 2026: antes de seguir con la detección de recurrentes, hay
    // que resolver de qué cuenta salió cada pago detectado a una tarjeta
    // (ver estado pagoOrigenDialogOpen más arriba) -- si hay alguno sin
    // responder todavía, se interrumpe acá igual que ya hace el diálogo
    // de candidatos a recurrente más abajo.
    // 21 sep 2026 (Augusto: "debería permitir importar todo a PCM pero
    // una vez dentro, colocar alertas de los problemas encontrados").
    //
    // Acá vivía el bloqueo: si un pago a tarjeta no tenía cuenta de
    // origen, el import se interrumpía y no entraba NADA hasta
    // responder. Ya no. Todo entra; lo que no se pudo resolver acá entra
    // marcado (ver revision_motivo en el backend), no suma en ningún
    // total, y se resuelve desde Revisión pendiente con el contexto a la
    // vista.
    //
    // El diálogo de origen sigue existiendo y se puede abrir a propósito
    // con "Origen de los pagos" -- resolverlo acá sigue siendo más rápido
    // que después. Lo que se quitó es la obligación.
    const historialParaDetectar = transactionsHistorial.map((h) => ({
      proveedor: h.proveedor,
      monto: h.monto,
      tipo: h.tipo,
      fecha: h.fecha,
    }));
    const nuevasParaDetectar = transactionsToImport
      .filter((t) => !t.es_recurrente) // ya marcada a mano, no hace falta preguntar
      .map((t) => ({
        proveedor: t.suggested_proveedor,
        monto: t.amount,
        tipo: t.transaction_type === 'income' ? 'ingreso' : 'gasto',
        fecha: t.date,
        categoria: t.suggested_category,
        _rowId: t.id,
      }));
    const candidatos = detectarCandidatosRecurrentes(
      nuevasParaDetectar,
      historialParaDetectar,
      [...recurringIncomes, ...recurringExpenses]
    );
    if (candidatos.length > 0) {
      setRecurringCandidatos(candidatos);
      setRecurringDialogOpen(true);
      return;
    }
    handleImportTransactions();
  };
  // 17 sep 2026: se llama al confirmar el diálogo "¿de qué cuenta salió
  // este pago?" -- guarda las elecciones y retoma el flujo normal de
  // handleClickImportar (detección de recurrentes + import) justo donde
  // se había quedado.
  const handleConfirmPagoOrigen = () => {
    setPagoOrigenDialogOpen(false);
    setPagosPendientesOrigen([]);
    // Si se abrió desde el botón de revisión (y no por un pago sin
    // responder), no hay que seguir con el import -- solo guardar.
    if (revisandoOrigenes) {
      setRevisandoOrigenes(false);
      return;
    }
    handleClickImportar();
  };

  // 21 sep 2026 (Augusto: "no me permite modificar de dónde salió el
  // dinero"). Reabre el mismo diálogo con TODOS los pagos a tarjeta, ya
  // respondidos incluidos, para poder corregir una elección. El caso que
  // lo motivó: eligió una cuenta en dólares con saldo 0, el import rebotó
  // con "saldo insuficiente", y no había forma de cambiar la respuesta
  // sin volver a empezar.
  const [revisandoOrigenes, setRevisandoOrigenes] = useState(false);

  const pagosATarjeta = useMemo(
    () =>
      parsedTransactions.filter((t) => {
        if (t.transaction_type !== 'income') return false;
        const account = detectedAccounts.find((a) => a._jobIndex === t._jobIndex);
        return account?.tipo_cuenta === 'tarjeta_credito';
      }),
    [parsedTransactions, detectedAccounts]
  );

  // 22 sep 2026 (Augusto: "al leer estados de cuenta de tarjetas de
  // crédito, cuando se vea Pago de tarjeta, se debe de reconocer que ese
  // pago se hizo de una cuenta sea esta de ahorros o cuenta corriente").
  //
  // Un pago que aparece en el extracto de la tarjeta salió de algún lado,
  // y casi siempre del mismo de siempre. El backend propone cuál (ver
  // GET /transactions/sugerir-origen-pago): la cuenta desde la que ya
  // pagaste esa tarjeta, o la única que calza.
  //
  // Se PROPONE, no se decide: queda preseleccionado y a la vista para
  // confirmar o cambiar. Elegir en silencio sería peor que preguntar --
  // un pago atribuido a la cuenta equivocada deja dos saldos mal y nada
  // que lo explique.
  const proponerOrigenes = useCallback((filas) => {
    const porTarjeta = new Map();
    filas.forEach((t) => {
      const cuenta = detectedAccounts.find((a) => a._jobIndex === t._jobIndex);
      if (cuenta?.account_id) porTarjeta.set(t.id, { accountId: cuenta.account_id, moneda: t.moneda });
    });
    porTarjeta.forEach(({ accountId, moneda }, rowId) => {
      apiClient
        .get('/transactions/sugerir-origen-pago', { params: { account_id: accountId, moneda: moneda || undefined } })
        .then((res) => {
          const sugerida = res.data?.cuenta_origen_id;
          if (!sugerida) return;
          // No pisa una elección que el usuario ya hizo.
          setPagoOrigenSeleccion((prev) => (prev[rowId] ? prev : { ...prev, [rowId]: sugerida }));
        })
        .catch(() => {});
    });
  }, [detectedAccounts]);

  const abrirRevisionOrigenes = () => {
    setRevisandoOrigenes(true);
    setPagosPendientesOrigen(pagosATarjeta);
    proponerOrigenes(pagosATarjeta);
    setPagoOrigenDialogOpen(true);
  };
  const handleConfirmRecurringCandidates = (elegidos) => {
    setRecurringDialogOpen(false);
    // Se pasan los ids elegidos directo a handleImportTransactions en vez
    // de actualizar parsedTransactions y esperar el próximo render -- así
    // no hay que lidiar con el timing de setState async justo antes de
    // leerlo de nuevo.
    const idsElegidos = new Set(elegidos.map((c) => c.nueva._rowId));
    handleImportTransactions(idsElegidos);
  };
  const handleCancelRecurringCandidates = () => {
    setRecurringDialogOpen(false);
    handleImportTransactions();
  };
  const handleImportTransactions = async (extraRecurrentIds = null) => {
    setLoading(true);
    try {
      // Preparar las transacciones para importar -- ver comentario en
      // handleClickImportar sobre por qué ya no cae a "todas" si
      // selectedRows queda vacía.
      const transactionsToImport = parsedTransactions.filter((t) => selectedRows.includes(t.id));
      // 3 ago 2026: agrupa por archivo de origen (_jobIndex) — cuando se
      // suben varios estados de cuenta a la vez, cada uno puede traer SU
      // PROPIA cuenta/tarjeta detectada (ej. una cuenta de ahorros y una
      // tarjeta de crédito juntas), así que hay que mandarle a cada grupo
      // su propio "account" en vez de uno solo para todo. Antes solo
      // existía un archivo (y por lo tanto una sola cuenta) a la vez.
      const groups = new Map(); // _jobIndex -> { account, transactions: [] }
      transactionsToImport.forEach((t) => {
        const key = t._jobIndex ?? -1;
        if (!groups.has(key)) {
          const account = detectedAccounts.find((a) => a._jobIndex === key) || null;
          groups.set(key, { account, transactions: [] });
        }
        groups.get(key).transactions.push(t);
      });
      let totalImported = 0;
      // 21 sep 2026: cuántos entraron marcados como pendientes de revisar.
      // Se acumula acá porque puede haber varios archivos en una misma
      // importación, cada uno con los suyos.
      let totalPendientes = 0;
      // 24 sep 2026: pagos de tarjeta reconocidos y unidos con su
      // contraparte en la cuenta (ver emparejar_pagos_de_tarjeta).
      let totalPagosUnidos = 0;
      // 24 sep 2026: cambios de moneda entre cuentas propias reconocidos.
      let totalCambiosUnidos = 0;
      // Pares que parecen un cambio de moneda pero no se pudieron
      // confirmar (ver REVISION_POSIBLE_CAMBIO en el backend).
      let totalCambiosPendientes = 0;
      const accountResults = [];
      for (const { account, transactions } of groups.values()) {
        // Formatear para el backend. OJO: el schema real (TransactionCreate,
        // backend_new/app/schemas/transaction.py) usa nombres en español
        // (tipo/monto/categoria/descripcion/fecha) — antes acá se mandaban
        // amount/description/category/transaction_type en inglés, que el
        // backend no reconoce como "tipo" ni "monto" (ambos requeridos), así
        // que cada importación fallaba en silencio con un 422 y no se
        // guardaba nada, aunque la pantalla de "¡Importación completada!"
        // llegara a mostrarse igual.
        const formattedTransactions = transactions.map(t => ({
          tipo: t.transaction_type === 'income' ? 'ingreso' : 'gasto',
          monto: t.amount,
          categoria: t.suggested_category,
          proveedor: t.suggested_proveedor || null,
          descripcion: t.description,
          fecha: new Date(t.date).toISOString(),
          // 5 ago 2026: solo viene distinto de null cuando ESTE movimiento
          // puntual está en una moneda distinta a la de la cuenta -- ver
          // getRowCurrencySymbol y moneda_override en statements.py.
          moneda: t.moneda_override || null,
          // 17 sep 2026: si esta fila es un pago a tarjeta y Augusto ya
          // eligió de qué cuenta salió (ver pagoOrigenSeleccion arriba),
          // el backend la crea como Transferencia real en vez de Ingreso.
          cuenta_origen_id: pagoOrigenSeleccion[t.id] || null,
          // 21 sep 2026: el backend usa esto para marcar la transacción
          // como pendiente de revisión en vez de que la fila se quede
          // afuera. duplicado_de_id es lo que después permite mostrar
          // "ya registrada: ..." sin tener que buscarla.
          posible_duplicado: !!t.posible_duplicado,
          duplicado_de_id: t.duplicado_de?.id || null,
        }));
        // Un solo endpoint (endpoints/statements.py -> /statements/import) crea
        // las transacciones Y, si se detectó de qué cuenta/tarjeta se trata,
        // crea o reconoce (por banco + últimos dígitos) la cuenta
        // correspondiente — las tarjetas de crédito también quedan como
        // Account (tipo "tarjeta_credito"), no como Deuda aparte — y enlaza
        // cada transacción a ella.
        const response = await apiClient.post('/statements/import', {
          account: account
            ? {
                entidad: account.entidad,
                tipo_cuenta: account.tipo_cuenta,
                nombre_producto: account.nombre_producto,
                ultimos_digitos: account.ultimos_digitos,
                // 25 sep 2026 (Augusto: "sin número" en Cuentas después de
                // importar, con el backend ya desplegado).
                //
                // Esta lista es explícita, campo por campo, y por eso
                // cualquier dato nuevo que el extractor aprenda a leer se
                // pierde acá salvo que alguien se acuerde de agregarlo.
                // numero_cuenta se leía bien del PDF, se mostraba en la
                // revisión, y moría en este objeto: nunca llegaba al
                // backend. Lo mismo venía pasando con dia_cierre y
                // fecha_corte desde el 28 de agosto -- por eso había que
                // escribir a mano la fecha de corte de cada tarjeta.
                numero_cuenta: account.numero_cuenta,
                dia_cierre: account.dia_cierre,
                fecha_corte: account.fecha_corte,
                moneda: account.moneda,
                saldo: account.saldo,
                // 3 ago 2026: si la tarjeta tiene saldo en dos monedas a la
                // vez (soles + dólares), el backend usa esto para crear una
                // segunda Deuda además de la principal — ver
                // _find_or_create_debt en statements.py.
                moneda_extranjera: account.moneda_extranjera,
                saldo_moneda_extranjera: account.saldo_moneda_extranjera,
              }
            : null,
          transactions: formattedTransactions,
          // 28 sep 2026 (Augusto: "al momento no tengo idea qué
          // importé"). El nombre del archivo se guarda desde el 25 de
          // septiembre en el backend, pero nunca llegaba: esta lista de
          // campos es explícita y no lo incluía. Tercer campo del día que
          // se pierde en el mismo lugar (ver numero_cuenta, dia_cierre).
          // _fileName lo pone mergeJobsForReview, que sabe de qué archivo
          // salió cada cuenta.
          archivo: account?._fileName || null,
        });
        totalImported += response.data.imported || 0;
        totalPendientes += response.data.pendientes || 0;
        totalPagosUnidos += response.data.pagos_unidos || 0;
        totalCambiosUnidos += response.data.cambios_unidos || 0;
        totalCambiosPendientes += response.data.cambios_pendientes || 0;
        if (response.data.account) accountResults.push(response.data);
        // 5 ago 2026: por cada fila que quedó marcada "Recurrente" (a mano
        // con el switch, o confirmada en el diálogo de candidatos de
        // handleClickImportar), además de la transacción normal recién
        // creada arriba se crea la serie recurrente correspondiente --
        // recién acá se conoce el account_id real (lo devuelve el import,
        // no existe antes si la cuenta se creó de nuevo en este mismo
        // paso).
        const accountId = response.data.account?.id ?? null;
        for (const t of transactions) {
          const marcadaRecurrente = t.es_recurrente || (extraRecurrentIds && extraRecurrentIds.has(t.id));
          if (!marcadaRecurrente) continue;
          const proveedor = (t.suggested_proveedor || '').trim();
          if (!proveedor) continue; // sin proveedor no hay con qué nombrar la serie
          const yaExiste = [...recurringIncomes, ...recurringExpenses].some(
            (r) => r.activo !== false && (r.nombre || '').trim().toLowerCase() === proveedor.toLowerCase()
          );
          if (yaExiste) continue; // ya es recurrente desde antes, no duplicar
          const tipo = t.transaction_type === 'income' ? 'ingreso' : 'gasto';
          const recurPayload = buildRecurringPayloadDesdeTransaccion({
            proveedor,
            monto: t.amount,
            tipo,
            fecha: t.date,
            categoria: t.suggested_category,
            accountId,
          });
          if (tipo === 'ingreso') {
            await dispatch(addIncome(recurPayload));
          } else {
            await dispatch(addExpense(recurPayload));
          }
        }
      }
      setImportResult({ imported: totalImported, pendientes: totalPendientes, pagosUnidos: totalPagosUnidos, cambiosUnidos: totalCambiosUnidos, cambiosPendientes: totalCambiosPendientes, accounts: accountResults });
      // Que el contador rojo aparezca en el acto, sin esperar al próximo
      // ciclo de un minuto -- es el momento en que la persona tiene el
      // tema en la cabeza.
      avisarRevisionesActualizadas();
      // Refresca la lista de transacciones en Redux para que el resto de la
      // app (dashboard, reportes) refleje lo recién importado.
      dispatch(fetchTransactions());
      // 5 ago 2026 (Augusto: "por qué una vez importada una nueva cuenta,
      // no actualiza las cuentas ingresadas? o el usuario tiene que hacer
      // refresh?"): sí tenía que hacer refresh. Un estado de cuenta puede
      // crear/actualizar una cuenta y, si es tarjeta de crédito, una o dos
      // deudas (ver _find_or_create_account/_find_or_create_debt en
      // statements.py) -- pero acá solo se refrescaban las transacciones.
      // Deudas (Deudas) sí vive en Redux (debtSlice), simplemente nunca se
      // volvía a pedir después de importar -- se arregla igual que
      // transacciones, con un dispatch más.
      dispatch(fetchDebts());
      // Cuentas (CuentasPage.js) todavía no vive en Redux -- carga su
      // lista una sola vez, en un useEffect con dependencias vacías, la
      // primera vez que se monta la página. No hay ningún mecanismo para
      // avisarle "algo cambió" desde acá (el diálogo de importar vive en
      // el header, montado aparte de cualquier página). En vez de migrar
      // toda la página a Redux solo para esto, se dispara un evento del
      // navegador -- CuentasPage.js escucha 'pcm:accounts-updated' y
      // vuelve a pedir sus cuentas cuando lo recibe.
      window.dispatchEvent(new CustomEvent('pcm:accounts-updated'));
      // Avanzar al paso final
      setActiveStep(2);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        detail ||
          (isSpanish ? 'Error al importar las transacciones.' : 'Error importing transactions.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    if (onClose) onClose();
  };

  // Columnas para la tabla de transacciones
  const columns = [
    {
      // 25 ago 2026 (Task #11 -- pedido de Augusto: avisar si un
      // movimiento del estado de cuenta podría ya estar registrado a
      // mano/WhatsApp/voz, pero sin bloquear ni saltar nada, decisión caso
      // por caso): el backend marca posible_duplicado en
      // _marcar_posibles_duplicados (statements.py) comparando cada
      // movimiento contra TODAS las transacciones ya existentes del
      // usuario -- mismo monto, mismo tipo (ingreso/gasto), fecha dentro
      // de ±3 días. Esta columna solo muestra el aviso -- no afecta
      // selección ni importación, el usuario decide con el checkbox de
      // cada fila si la incluye o no.
      field: 'posible_duplicado',
      headerName: '',
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        params.row.posible_duplicado ? (
          <Tooltip
            title={
              isSpanish
                ? 'Ya tienes una transacción con el mismo monto, el mismo tipo, y una fecha cercana (± 3 días). Podría ser la misma registrada dos veces -- revísala antes de importar.'
                : 'You already have a transaction with the same amount, same type, and a nearby date (± 3 days). It might be the same one recorded twice -- review it before importing.'
            }
          >
            <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />
          </Tooltip>
        ) : null
      ),
    },
    {
      field: 'date',
      headerName: isSpanish ? 'Fecha' : 'Date',
      flex: 1,
      minWidth: 110,
      editable: true,
    },
    {
      field: 'description',
      headerName: isSpanish ? 'Descripción' : 'Description',
      flex: 2,
      minWidth: 200,
      editable: true,
    },
    {
      field: 'amount',
      headerName: isSpanish ? 'Monto' : 'Amount',
      flex: 1,
      minWidth: 120,
      editable: true,
      type: 'number',
      renderCell: (params) => (
        <Typography
          color={params.row.transaction_type === 'income' ? 'success.main' : 'error.main'}
          fontWeight="medium"
        >
          {params.row.transaction_type === 'income' ? '+' : '-'}
          {getRowCurrencySymbol(params.row)}{Math.abs(params.row.amount).toFixed(2)}
          {params.row.moneda_override && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({params.row.moneda_override})
            </Typography>
          )}
        </Typography>
      )
    },
    {
      field: 'suggested_category',
      headerName: isSpanish ? 'Categoría' : 'Category',
      flex: 1.5,
      minWidth: 160,
      editable: true,
      type: 'singleSelect',
      valueOptions: allCategories,
      renderCell: (params) => {
        const category = params.value;
        const categoryLabel = allCategories.find(c => c.value === category)?.label || category;
        return (
          <CategoryChip
            label={categoryLabel}
            icon={getCategoryIcon(category)}
            color={getCategoryColor(category)}
            size="small"
          />
        );
      }
    },
    {
      field: 'suggested_proveedor',
      headerName: isSpanish ? 'Proveedor' : 'Merchant',
      flex: 1.2,
      minWidth: 140,
      editable: true,
      renderCell: (params) => params.value || '—',
    },
    {
      // 5 ago 2026 (Augusto: "sería bueno tener un switch de recurrente
      // para que el usuario seleccione para consumos recurrentes a la
      // hora de importar estados de cuenta"): además del diálogo
      // automático de candidatos (ver handleClickImportar), el usuario
      // puede marcar cualquier fila a mano de una vez -- por ejemplo un
      // primer pago de un servicio nuevo que todavía no tiene una
      // ocurrencia anterior para que el detector la reconozca sola.
      field: 'es_recurrente',
      headerName: isSpanish ? 'Recurrente' : 'Recurring',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Tooltip
          title={
            isSpanish
              ? '¿Se repite todos los meses? (luz, agua, suscripciones...)'
              : 'Does it repeat every month? (utilities, subscriptions...)'
          }
        >
          <Switch
            size="small"
            checked={!!params.row.es_recurrente}
            onChange={(e) => {
              const checked = e.target.checked;
              setParsedTransactions((prev) =>
                prev.map((t) => (t.id === params.row.id ? { ...t, es_recurrente: checked } : t))
              );
            }}
          />
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: isSpanish ? 'Acciones' : 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEditTransaction(params.row)}
            sx={{ color: goldColors.main }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  // Contenido para cada paso
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {/* 4 ago 2026: elegir entre el importador con IA (PDF, sujeto a
                cuota) y el importador gratis sin IA (CSV/Excel, sin cuota
                ni requisito de plan pago -- ver bulk_import.py). Cambiar
                de modo limpia los archivos ya elegidos porque cada uno
                acepta una extensión distinta. */}
            <ToggleButtonGroup
              exclusive
              value={importMode}
              onChange={(e, value) => {
                if (!value) return;
                setImportMode(value);
                setSelectedFiles([]);
                setJobs([]);
                setError(null);
              }}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="pdf">
                {isSpanish ? 'PDF con IA' : 'PDF with AI'}
              </ToggleButton>
              <ToggleButton value="csv">
                {isSpanish ? 'CSV/Excel (gratis)' : 'CSV/Excel (free)'}
              </ToggleButton>
            </ToggleButtonGroup>
            {importMode === 'csv' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {isSpanish
                  ? 'Sin costo y sin límite de páginas -- disponible en todos los planes, incluido el gratuito.'
                  : 'No cost and no page limit -- available on every plan, including free.'}
                {' '}
                <Button size="small" onClick={downloadCsvTemplate} sx={{ ml: 1 }}>
                  {isSpanish ? 'Descargar plantilla' : 'Download template'}
                </Button>
              </Alert>
            )}
            {/* 4 ago 2026: aviso de cuota de páginas del mes (ver
                backend_new/app/core/plans.py) -- unlimited=true es Negocio
                Pro (pay-as-you-go, sin tope ni paquetes que ofrecer). Solo
                aplica al modo PDF con IA -- el importador CSV/Excel no
                consume cuota.
                5 ago 2026 (Augusto: "creo que este mensaje debe de ser más
                visual, sobre todo el tema de comprar más"): antes era un
                Alert de una sola línea con un botón de texto chiquito al
                costado -- fácil de pasar por alto justo cuando es más
                importante (0 páginas disponibles). Ahora es una tarjeta con
                un ícono, el número de páginas destacado, y un botón
                "Comprar páginas" contained (no solo texto) que se nota. */}
            {importMode === 'pdf' && quota && !quota.unlimited && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 2,
                  borderColor: quota.pages_available_total === 0 ? 'warning.main' : goldColors.light,
                  bgcolor:
                    quota.pages_available_total === 0
                      ? alpha(theme.palette.warning.main, 0.08)
                      : alpha(goldColors.main, 0.06),
                }}
              >
                {/* 5 ago 2026 (Augusto: "no quiero que el usuario tenga que
                    scroll down para ver los detalles del import"): tarjeta
                    más compacta (ícono, textos y padding reducidos, y el
                    toggle de admin movido a la misma fila en vez de una
                    segunda fila con su propio borde) para que el paso 1
                    quepa sin scroll junto con la zona de arrastrar archivo. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: quota.pages_available_total === 0 ? 'warning.main' : goldColors.main,
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    {quota.pages_available_total === 0 ? (
                      <ErrorIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <DescriptionIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
                      {isSpanish
                        ? `${quota.pages_available_total} páginas disponibles este mes`
                        : `${quota.pages_available_total} pages available this month`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isSpanish
                        ? `${quota.pages_remaining_included} de tu plan`
                        : `${quota.pages_remaining_included} from your plan`}
                      {quota.extra_pages_balance
                        ? isSpanish
                          ? ` + ${quota.extra_pages_balance} extra que ya tienes`
                          : ` + ${quota.extra_pages_balance} extra you already have`
                        : ''}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => setBuyDialogOpen(true)}
                    sx={{
                      bgcolor: goldColors.main,
                      '&:hover': { bgcolor: goldColors.dark },
                      whiteSpace: 'nowrap',
                      borderRadius: '8px',
                    }}
                  >
                    {isSpanish ? 'Comprar páginas' : 'Buy pages'}
                  </Button>
                  {/* 5 ago 2026 (Augusto: "no veo la opción de Ignorar para
                      mi cuenta"): visible desde el principio para cuentas
                      admin, ahora en la misma fila para no sumar alto. */}
                  {isAdminUser && quota.pages_available_total === 0 && (
                    adminBypassQuota ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        color="success"
                        size="small"
                        label={isSpanish ? 'Cuota ignorada' : 'Quota bypassed'}
                        onDelete={() => setAdminBypassQuota(false)}
                      />
                    ) : (
                      <Button size="small" onClick={() => setAdminBypassQuota(true)} sx={{ whiteSpace: 'nowrap' }}>
                        {isSpanish ? 'Ignorar y continuar' : 'Ignore and continue'}
                      </Button>
                    )
                  )}
                </Box>
              </Paper>
            )}
            {importMode === 'pdf' && quota && quota.unlimited && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {isSpanish
                  ? 'Tu cuenta no tiene límite de páginas.'
                  : 'Your account has no page limit.'}
              </Alert>
            )}
            <UploadContainer
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleUploadClick}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept={importMode === 'csv' ? '.csv,.xlsx' : '.pdf'}
                multiple
              />
              {/* 5 ago 2026: ícono y textos achicados junto con el padding
                  del contenedor (ver UploadContainer arriba) para que este
                  paso quepa sin scroll. */}
              <UploadFileIcon sx={{ fontSize: 44, color: goldColors.main, mb: 1 }} />
              <Typography variant="subtitle1" align="center" gutterBottom>
                {isSpanish ? 'Arrastra tus archivos aquí o haz clic para seleccionarlos' : 'Drag your files here or click to select'}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {importMode === 'csv'
                  ? isSpanish
                    ? 'CSV o Excel (.xlsx) — usa la plantilla de arriba como guía de columnas. Puedes elegir varios a la vez.'
                    : 'CSV or Excel (.xlsx) — use the template above as a column guide. You can pick several at once.'
                  : isSpanish
                    ? 'Solo PDF — con texto o escaneado, ambos funcionan. Puedes elegir varios a la vez.'
                    : 'PDF only — text-based or scanned, both work. You can pick several at once.'}
              </Typography>
            </UploadContainer>
            {/* 3 ago 2026 (pedido de Augusto): lista de archivos elegidos,
                fuera del área clicable de arriba para que el botón de
                quitar (x) de cada chip no dispare también el selector de
                archivos. */}
            {selectedFiles.length > 0 && !loading && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedFiles.map((file, idx) => (
                  <Chip
                    key={`${file.name}-${file.size}-${idx}`}
                    icon={<DescriptionIcon />}
                    label={file.name}
                    onDelete={() => handleRemoveFile(idx)}
                    sx={{ bgcolor: alpha(goldColors.main, 0.08) }}
                  />
                ))}
              </Box>
            )}
            {/* 25 sep 2026 (Augusto: "hay un máximo de pdf's que se puedan
                subir al mismo tiempo?"). Máximo no hay; lo que faltaba era
                decirle qué iba a pasar antes de apretar el botón. */}
            {resumenSeleccion && !loading && jobs.length === 0 && (
              <Alert severity={resumenSeleccion.noAlcanza ? 'warning' : 'info'} sx={{ mt: 2 }}>
                {isSpanish ? (
                  <>
                    {resumenSeleccion.archivos === 1
                      ? '1 archivo'
                      : `${resumenSeleccion.archivos} archivos`}
                    {resumenSeleccion.paginas > 0 && (
                      <>
                        {' · '}
                        {resumenSeleccion.sinContar > 0 ? 'más de ' : ''}
                        {resumenSeleccion.paginas}
                        {resumenSeleccion.paginas === 1 ? ' página' : ' páginas'} de tu cuota
                      </>
                    )}
                    {resumenSeleccion.archivos > 1 &&
                      ' · se procesan de a uno, así que puede tomar varios minutos'}
                    {resumenSeleccion.noAlcanza && (
                      <>
                        {'. '}
                        <strong>
                          Te quedan {resumenSeleccion.disponibles} páginas este mes: no alcanzan
                          para todo el lote. Los primeros archivos van a entrar y los últimos se
                          van a frenar.
                        </strong>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {resumenSeleccion.archivos === 1 ? '1 file' : `${resumenSeleccion.archivos} files`}
                    {resumenSeleccion.paginas > 0 && (
                      <>
                        {' · '}
                        {resumenSeleccion.sinContar > 0 ? 'more than ' : ''}
                        {resumenSeleccion.paginas}
                        {resumenSeleccion.paginas === 1 ? ' page' : ' pages'} from your quota
                      </>
                    )}
                    {resumenSeleccion.archivos > 1 &&
                      ' · they are processed one at a time, so it may take several minutes'}
                    {resumenSeleccion.noAlcanza && (
                      <>
                        {'. '}
                        <strong>
                          You have {resumenSeleccion.disponibles} pages left this month, not enough
                          for the whole batch. The first files will go through and the last ones
                          will stop.
                        </strong>
                      </>
                    )}
                  </>
                )}
              </Alert>
            )}
            {/* Progreso por archivo: cada uno es su propio job en el
                backend (ver createStatementJob/pollJobUntilDone), así que
                se muestran juntos pero avanzan de forma independiente. */}
            {jobs.length > 0 && (
              <List dense sx={{ mt: 2 }}>
                {jobs.map((job, idx) => (
                  <ListItem
                    key={job.id || `${job.fileName}-${idx}`}
                    divider={idx < jobs.length - 1}
                    secondaryAction={
                      job.quotaExceeded ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => setBuyDialogOpen(true)}>
                            {isSpanish ? 'Comprar páginas' : 'Buy pages'}
                          </Button>
                          {/* 5 ago 2026: solo admins -- ver isAdminUser. El
                              backend vuelve a validar el email antes de
                              saltarse la cuota (ver admin_override en
                              statements.py), así que ocultar el botón acá
                              es solo cosmético, no la única barrera. */}
                          {isAdminUser && (
                            <Button size="small" variant="text" onClick={() => handleAdminIgnoreQuota(idx)}>
                              {isSpanish ? 'Ignorar y continuar' : 'Ignore and continue'}
                            </Button>
                          )}
                        </Box>
                      ) : null
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {job.status === 'done' && <CheckCircleIcon color="success" fontSize="small" />}
                      {job.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                      {job.status === 'processing' && <CircularProgress size={18} sx={{ color: goldColors.main }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={job.fileName}
                      secondary={
                        job.status === 'error'
                          ? job.error
                          : job.status === 'done'
                            ? (isSpanish ? `${job.transactionsCount} movimientos encontrados` : `${job.transactionsCount} transactions found`) +
                              (job.error ? ` — ${job.error}` : '')
                            : processingMessage
                      }
                      secondaryTypographyProps={{ color: job.status === 'error' ? 'error' : 'text.secondary' }}
                      sx={job.quotaExceeded ? { mr: isAdminUser ? 28 : 16 } : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {isSpanish
                ? `Transacciones encontradas: ${parsedTransactions.length}`
                : `Transactions found: ${parsedTransactions.length}`}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isSpanish
                ? 'Haz doble clic en cualquier celda para editarla directamente. Al cambiar la categoría o el proveedor de una fila, se aplica también a las demás con el mismo proveedor.'
                : 'Double-click any cell to edit it directly. Changing a category or merchant applies it to other rows with the same merchant too.'}
            </Typography>
            {autoAppliedNotice && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAutoAppliedNotice(null)}>
                {autoAppliedNotice}
              </Alert>
            )}
            {/* 25 ago 2026 (Task #11) / reescrito 21 sep 2026 (Augusto:
                "no me da opción alguna para hacer algo con esta supuesta
                operación duplicada").

                El aviso decía cuántos posibles duplicados había pero no
                que estuvieran EXCLUIDOS del import (eso cambió el 28 de
                agosto y este texto se quedó viejo). Resultado: alguien
                importaba 78 de 81 movimientos creyendo que iban los 81, y
                los 3 que faltaban desaparecían sin dejar rastro. Ahora
                dice qué está pasando y ofrece las dos salidas de una vez,
                sin obligar a ir fila por fila. */}
            {parsedTransactions.some((t) => t.posible_duplicado) && (() => {
              const duplicados = parsedTransactions.filter((t) => t.posible_duplicado);
              const incluidos = duplicados.filter((t) => selectedRows.includes(t.id)).length;
              const fuera = duplicados.length - incluidos;
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 260 }}>
                      {isSpanish
                        ? `${duplicados.length} movimiento(s) se parecen a transacciones que ya tienes (mismo monto, mismo tipo, fecha dentro de 3 días).`
                        : `${duplicados.length} transaction(s) look like ones you already have (same amount, same type, date within 3 days).`}
                      {fuera > 0 && (
                        <strong>
                          {isSpanish
                            ? ` ${fuera} de ellos NO se va(n) a importar.`
                            : ` ${fuera} of them will NOT be imported.`}
                        </strong>
                      )}
                      {' '}
                      {isSpanish
                        ? 'Ábrelos con el lápiz para ver contra cuál chocan y decidir.'
                        : 'Open them with the pencil to see what they collide with and decide.'}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={fuera === 0}
                        onClick={() =>
                          setSelectedRows((prev) => [
                            ...prev,
                            ...duplicados.filter((t) => !prev.includes(t.id)).map((t) => t.id),
                          ])
                        }
                      >
                        {isSpanish ? 'Importarlos igual' : 'Import them anyway'}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={incluidos === 0}
                        onClick={() =>
                          setSelectedRows((prev) =>
                            prev.filter((id) => !duplicados.some((t) => t.id === id))
                          )
                        }
                      >
                        {isSpanish ? 'Dejarlos fuera' : 'Leave them out'}
                      </Button>
                    </Box>
                  </Box>
                </Alert>
              );
            })()}
            {/* 3 ago 2026: puede haber más de una cuenta detectada cuando se
                suben varios estados de cuenta a la vez (uno por archivo) —
                antes solo existía detectedAccount, singular. */}
            {detectedAccounts.map((account) => (
              <Alert
                key={account._jobIndex}
                severity="info"
                icon={account.tipo_cuenta === 'tarjeta_credito' ? <CreditCardIcon /> : <AccountBalanceIcon />}
                sx={{ mb: 1 }}
              >
                {isSpanish ? 'Se detectó: ' : 'Detected: '}
                <strong>{getAccountKindLabel(account.tipo_cuenta)}</strong>
                {account.entidad ? ` — ${account.entidad}` : ''}
                {account.nombre_producto ? ` (${account.nombre_producto})` : ''}
                {account.ultimos_digitos ? ` ****${account.ultimos_digitos}` : ''}
                {' '}({account._fileName}).{' '}
                {isSpanish
                  ? 'Se creará automáticamente o se vinculará a una ya existente al importar.'
                  : 'It will be created automatically or linked to an existing one when you import.'}
                {/* 30 oct 2026 (Augusto: "no aparecen los cargos en dólares en
                    BBVA Visa"): lo que la lectura probablemente hizo mal,
                    dicho ANTES de importar. */}
                {(account._avisos || []).map((aviso) => (
                  <Typography key={aviso} variant="body2" sx={{ mt: 1, color: 'warning.dark', fontWeight: 600 }}>
                    ⚠ {aviso}
                  </Typography>
                ))}
              </Alert>
            ))}
            <Box sx={{ height: 400, width: '100%', mt: 2 }}>
              <StyledDataGrid
                rows={parsedTransactions}
                columns={columns}
                pageSize={5}
                rowsPerPageOptions={[5, 10, 20]}
                checkboxSelection
                disableSelectionOnClick
                onSelectionModelChange={(newSelection) => {
                  setSelectedRows(newSelection);
                }}
                selectionModel={selectedRows}
                loading={loading}
                processRowUpdate={handleProcessRowUpdate}
                onProcessRowUpdateError={() => {
                  // La validación de handleProcessRowUpdate ya evita valores
                  // inválidos (montos NaN, descripciones vacías) -- esto solo
                  // cubre errores inesperados para que la celda no se quede
                  // atascada en modo edición.
                }}
                getRowClassName={(params) => (params.row.posible_duplicado ? 'fila-posible-duplicado' : '')}
              />
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mt: 3 }}
                /* 21 sep 2026: el error más común acá es "saldo
                   insuficiente" en la cuenta de origen que se eligió para
                   un pago de tarjeta. Antes el mensaje no venía con
                   ninguna salida -- la elección ya estaba guardada y el
                   diálogo no volvía a aparecer. Ahora el arreglo está
                   pegado al problema. */
                action={
                  pagosATarjeta.length > 0 ? (
                    <Button color="inherit" size="small" onClick={abrirRevisionOrigenes}>
                      {isSpanish ? 'Cambiar cuenta de origen' : 'Change source account'}
                    </Button>
                  ) : null
                }
              >
                {error}
              </Alert>
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {isSpanish ? '¡Importación completada!' : 'Import Completed!'}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {isSpanish
                ? `${importResult?.imported ?? (selectedRows.length || parsedTransactions.length)} transacciones han sido añadidas a tu cuenta.`
                : `${importResult?.imported ?? (selectedRows.length || parsedTransactions.length)} transactions have been added to your account.`}
            </Typography>
            {/* 21 sep 2026: entró todo, pero algo puede haber quedado
                esperando una decisión. Decirlo acá, con el camino a
                seguir, es lo que evita que el contador rojo aparezca
                después sin que nadie sepa de dónde salió. */}
            {/* 24 sep 2026 (Augusto: "necesito que PCM identifique estas
                transferencias... automáticamente"). Se dice cuántos pagos
                se reconocieron: si no, los ingresos y gastos del mes
                bajan solos después de importar y no hay forma de saber
                por qué. */}
            {importResult?.pagosUnidos > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {isSpanish
                  ? `Se reconocieron ${importResult.pagosUnidos} pago(s) de tarjeta: el egreso de tu cuenta y el ingreso en la tarjeta eran el mismo movimiento, así que quedaron unidos como una transferencia. Dejan de contar como ingreso y como gasto — antes inflaban los dos.`
                  : `${importResult.pagosUnidos} card payment(s) were recognised: the expense in your account and the income on the card were the same movement, so they were merged into a transfer. They no longer count as income or spending — before, they inflated both.`}
              </Alert>
            )}
            {importResult?.cambiosUnidos > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {isSpanish
                  ? `Se reconocieron ${importResult.cambiosUnidos} cambio(s) de moneda entre tus cuentas: el monto que salió de una y el que entró a la otra eran el mismo movimiento, así que quedaron unidos como una transferencia. Dejan de contar como gasto e ingreso.`
                  : `${importResult.cambiosUnidos} currency exchange(s) between your accounts were recognised: the amount leaving one and the amount arriving in the other were the same movement, so they were merged into a transfer. They no longer count as spending and income.`}
              </Alert>
            )}
            {importResult?.cambiosPendientes > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {isSpanish
                  ? `${importResult.cambiosPendientes} par(es) de movimientos parecen un cambio de moneda entre tus cuentas, pero PCM no tiene tipo de cambio para confirmarlo. Quedaron fuera de tus totales hasta que decidas: revísalos en Revisión pendiente.`
                  : `${importResult.cambiosPendientes} pair(s) look like a currency exchange between your accounts, but PCM has no exchange rate to confirm it. They are out of your totals until you decide — check them under Pending review.`}
              </Alert>
            )}
            {importResult?.pendientes > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 2, textAlign: 'left' }}
                action={
                  <Button color="inherit" size="small" onClick={() => navigate('/revision')}>
                    {isSpanish ? 'Revisar ahora' : 'Review now'}
                  </Button>
                }
              >
                {isSpanish
                  ? `${importResult.pendientes} de ellas necesitan una decisión tuya (posible duplicado o pago sin cuenta de origen). Hasta que las resuelvas NO suman en tu tablero ni en tus informes.`
                  : `${importResult.pendientes} of them need a decision from you (possible duplicate, or a payment with no source account). Until you resolve them they do NOT count toward your dashboard or reports.`}
              </Alert>
            )}
            {/* 5 ago 2026 (pedido de Augusto: "mejora como se muestran esos
                mensajes"): antes cada Alert era display:'inline-flex' sin un
                contenedor con flex-wrap, así que el navegador las acomodaba
                en un mosaico de dos columnas desparejo (una cuenta al lado
                de una deuda, la siguiente deuda sola en la fila de abajo) y
                cada línea era una sola oración larga en texto corrido. Acá
                se agrupan las deudas DENTRO de la Alert de su propia cuenta
                (antes eran Alerts sueltas al mismo nivel, repitiendo
                "Visa Signature A (BBVA)" en cada una cuando la tarjeta tiene
                línea en soles y en dólares a la vez), todo en una columna de
                ancho fijo con jerarquía visual clara: etiqueta pequeña,
                nombre en negrita, monto destacado. */}
            <Box
              sx={{
                mt: 1,
                mb: 1,
                mx: 'auto',
                maxWidth: 480,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {importResult?.accounts?.map((acc, idx) => (
                <Alert
                  key={idx}
                  // 25 sep 2026 (Augusto: "importé 3 cuentas... PCM solo
                  // muestra 1 sola"). Cuando el extracto cae en una cuenta
                  // que YA existía, hay que decirlo fuerte: si era la
                  // cuenta equivocada, es el momento de darse cuenta, no
                  // tres importaciones después.
                  severity={acc.account_created ? 'success' : 'info'}
                  icon={acc.account.tipo === 'tarjeta_credito' ? <CreditCardIcon /> : <AccountBalanceIcon />}
                  sx={{ textAlign: 'left' }}
                >
                  <Typography variant="caption" color="text.secondary" component="div">
                    {acc.account_created
                      ? (isSpanish ? 'Cuenta creada' : 'Account created')
                      : (isSpanish
                        ? 'Se usó una cuenta que ya tenías'
                        : 'Matched an account you already had')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} component="div">
                    {acc.account.nombre}
                    {acc.account.entidad ? ` · ${acc.account.entidad}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    {isSpanish ? 'Saldo: ' : 'Balance: '}
                    <strong>{acc.account.saldo?.toFixed(2)} {acc.account.moneda}</strong>
                  </Typography>
                  {!acc.account_created && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                      {isSpanish
                        ? 'Si esta no era la cuenta del extracto que subiste, corrígela en Cuentas antes de seguir importando.'
                        : "If this wasn't the account from the statement you uploaded, fix it under Accounts before importing more."}
                    </Typography>
                  )}
                  {/* 3 ago 2026: si la cuenta importada era una tarjeta de
                      crédito, el backend también crea/actualiza una Deuda
                      (Debt) con el total adeudado — una por moneda si la
                      tarjeta tiene línea en soles y en dólares a la vez (ver
                      _find_or_create_debt en statements.py). Se muestran acá
                      adentro, no como Alerts propias, porque son la misma
                      tarjeta que ya se identificó arriba. */}
                  {(acc.debts || []).length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      {acc.debts.map((debt, dIdx) => (
                        <Typography key={dIdx} variant="body2" color="text.secondary" component="div">
                          {debt.created
                            ? (isSpanish ? 'Deuda registrada: ' : 'Debt recorded: ')
                            : (isSpanish ? 'Deuda actualizada: ' : 'Debt updated: ')}
                          <strong>{debt.monto?.toFixed(2)} {debt.moneda}</strong>
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Alert>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFinish}
                sx={{
                  borderRadius: '8px',
                  bgcolor: goldColors.main,
                  '&:hover': { bgcolor: goldColors.dark }
                }}
              >
                {isSpanish ? 'Finalizar' : 'Finish'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };
  // 5 ago 2026 (Augusto: "cuando trato de importar un documento,
  // desaparece la opción de Procesar Archivo, se esconde abajo, y tengo
  // que hacer scroll down"): antes el botón de acción de cada paso vivía
  // AL FINAL del contenido de ese paso (dentro de renderStepContent), así
  // que si el paso tenía mucho contenido (aviso de cuota + lista de
  // archivos + progreso de cada job + error), el botón quedaba empujado
  // fuera de la vista y había que scrollear el diálogo entero para
  // encontrarlo. Ahora el botón de cada paso vive en un pie FIJO, fuera
  // del área que scrollea (ver el return más abajo) -- siempre visible
  // sin importar cuánto contenido tenga el paso. El paso 2 (confirmación)
  // no necesita esto: su botón "Finalizar" sigue centrado dentro de su
  // propio contenido, porque ese paso es corto y nunca requiere scroll.
  const renderStepFooter = () => {
    if (activeStep === 0) {
      return (
        <Button
          variant="contained"
          color="primary"
          disabled={selectedFiles.length === 0 || loading}
          onClick={handleProcessFiles}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FileUploadIcon />}
          sx={{
            borderRadius: '8px',
            bgcolor: goldColors.main,
            '&:hover': { bgcolor: goldColors.dark }
          }}
        >
          {loading
            ? (isSpanish ? 'Procesando...' : 'Processing...')
            : (isSpanish
                ? `Procesar ${selectedFiles.length > 1 ? `${selectedFiles.length} archivos` : 'archivo'}`
                : `Process ${selectedFiles.length > 1 ? `${selectedFiles.length} files` : 'file'}`)}
        </Button>
      );
    }
    if (activeStep === 1) {
      return (
        <>
          <Button
            variant="outlined"
            onClick={() => setActiveStep(0)}
            sx={{ mr: 2, borderRadius: '8px' }}
          >
            {isSpanish ? 'Atrás' : 'Back'}
          </Button>
          {/* 24 sep 2026 (Augusto: "si ya esto funciona, entonces no hay
              necesidad de decir de qué cuenta salió el pago de tarjeta").
              Tenía razón: desde que el import reconoce solo los pagos
              (ver emparejar_pagos_de_tarjeta en el backend), preguntarlo
              acá es trabajo que en la mayoría de los casos sobra.
              Acá vivía el botón "Origen de los pagos".
              Lo que no se pueda emparejar --porque el extracto de la
              cuenta no se importó-- entra marcado y NO suma en ningún
              total hasta resolverse en Revisión pendiente. Ahí sigue
              estando la pregunta, para el caso en que haga falta. */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleClickImportar}
            disabled={loading || selectedRows.length === 0}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadDoneIcon />}
            sx={{
              borderRadius: '8px',
              bgcolor: goldColors.main,
              '&:hover': { bgcolor: goldColors.dark }
            }}
          >
            {/* 28 ago 2026: ya no cae a "todas" -- selectedRows siempre es
                la cuenta real que se va a importar (ver comentario en
                handleClickImportar). */}
            {loading
              ? (isSpanish ? 'Importando...' : 'Importing...')
              : (isSpanish
                  ? `Importar ${selectedRows.length} transacciones`
                  : `Import ${selectedRows.length} transactions`)}
          </Button>
        </>
      );
    }
    return null;
  };
  return (
    <Paper sx={{
      width: '100%',
      p: 0,
      borderRadius: '8px',
      overflow: 'hidden',
      // 5 ago 2026 (Augusto: "no quiero que el usuario tenga que scroll
      // down para ver los detalles del import"): 82vh se quedaba corto en
      // ventanas de altura normal apenas se agregó el aviso de cuota más
      // visual (ver renderStepContent, paso 0) -- el contenido del paso 1
      // (aviso + zona de arrastrar archivo) empezaba a cortarse y obligaba
      // a scrollear el área interna. 92vh + achicar un poco el aviso de
      // cuota y la zona de arrastrar (ver más abajo) deja todo visible sin
      // scroll en la gran mayoría de tamaños de ventana.
      height: '92vh',
      maxHeight: '92vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Encabezado (fijo) */}
      <Box sx={{
        bgcolor: theme.palette.mode === 'dark' ? alpha('#000', 0.6) : alpha('#f5f5f5', 0.6),
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        flexShrink: 0
      }}>
        {/* 3 ago 2026 (pedido de Augusto): antes, si la importación fallaba
            (ej. error de la IA), no había ninguna forma visible de cerrar
            esta ventana -- solo "Finalizar" al terminar con éxito, o hacer
            clic fuera del diálogo (nada obvio). Esta X siempre visible
            deja salir en cualquier paso, con o sin error. */}
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12 }}
            aria-label={isSpanish ? 'Cerrar' : 'Close'}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h5" fontWeight="medium" color="primary">
          {isSpanish ? 'Importar Estado de Cuenta' : 'Import Bank Statement'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {steps[activeStep].description}
        </Typography>
      </Box>
      {/* Stepper (fijo) */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {/* Contenido principal (con scroll propio -- el pie de abajo con el
          botón de acción queda siempre visible, sin importar cuánto crezca
          esto) */}
      <Box sx={{ p: 3, flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {renderStepContent()}
      </Box>
      {/* Pie fijo con el botón de acción del paso actual (ver
          renderStepFooter) -- ausente en el paso 2, que ya trae su propio
          botón centrado dentro del contenido. */}
      {renderStepFooter() && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0
          }}
        >
          {renderStepFooter()}
        </Box>
      )}
      <RecurringCandidatesDialog
        open={recurringDialogOpen}
        candidatos={recurringCandidatos}
        onConfirm={handleConfirmRecurringCandidates}
        onCancel={handleCancelRecurringCandidates}
        isSpanish={isSpanish}
      />
      {/* 17 sep 2026 (pedido de Augusto): antes de importar, si hay pagos
          detectados en un estado de cuenta de tarjeta, se pregunta de qué
          cuenta salió cada uno -- ver handleClickImportar/
          handleConfirmPagoOrigen más arriba. */}
      <Dialog open={pagoOrigenDialogOpen} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isSpanish ? '¿De qué cuenta salió este pago?' : 'Which account did this payment come from?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSpanish
              ? 'Estos movimientos redujeron el saldo de tu tarjeta -- para que PCM los registre como una Transferencia real (no como un Ingreso), indica de qué cuenta salió el dinero en cada caso.'
              : 'These lines reduced your card balance -- so PCM can record them as a real Transfer (not an Income), tell us which account the money came from.'}
          </Typography>
          {pagosPendientesOrigen.map((t) => (
            <Box
              key={t.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}
            >
              <Box sx={{ minWidth: 160 }}>
                <Typography variant="body2">
                  {new Date(t.date).toLocaleDateString(isSpanish ? 'es-PE' : 'en-US')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {getRowCurrencySymbol(t)} {Number(t.amount).toFixed(2)}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel id={`pago-origen-${t.id}`}>{isSpanish ? 'Cuenta de origen' : 'Source account'}</InputLabel>
                <Select
                  labelId={`pago-origen-${t.id}`}
                  label={isSpanish ? 'Cuenta de origen' : 'Source account'}
                  value={pagoOrigenSeleccion[t.id] || ''}
                  onChange={(e) => {
                    if (e.target.value === '__crear_cuenta__') {
                      abrirNuevaCuentaOrigen(t.id);
                      return;
                    }
                    setPagoOrigenSeleccion((prev) => ({ ...prev, [t.id]: e.target.value }));
                  }}
                >
                  {cuentasOrigenElegibles.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {esCuentaDeActivo(c) ? '' : '💳 '}{c.nombre}
                      {c.moneda ? ` (${c.moneda})` : ''}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="__crear_cuenta__">
                    {isSpanish ? '+ Crear cuenta nueva…' : '+ Create new account…'}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          {/* Cancelar solo tiene sentido cuando se entró a revisar por
              gusto. Si hay pagos sin responder, salir dejaría el import a
              medio configurar. */}
          {revisandoOrigenes && (
            <Button
              onClick={() => {
                setPagoOrigenDialogOpen(false);
                setPagosPendientesOrigen([]);
                setRevisandoOrigenes(false);
              }}
            >
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
          )}
          <Button
            onClick={handleConfirmPagoOrigen}
            variant="contained"
            disabled={pagosPendientesOrigen.some((t) => !pagoOrigenSeleccion[t.id])}
          >
            {revisandoOrigenes
              ? (isSpanish ? 'Guardar' : 'Save')
              : (isSpanish ? 'Continuar' : 'Continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 21 sep 2026 (Augusto: "debería permitir añadir una nueva al
          seleccionar la fuente"): alta rápida sin salir del importador.
          Salir significaría perder el archivo ya procesado con IA, que
          además cuesta plata volver a procesar. */}
      <Dialog
        open={openNuevaCuentaOrigen}
        onClose={() => setOpenNuevaCuentaOrigen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{isSpanish ? 'Nueva cuenta' : 'New account'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={isSpanish ? 'Nombre' : 'Name'}
            placeholder={isSpanish ? 'BCP Ahorros Soles' : 'Checking account'}
            value={nuevaCuentaOrigen.nombre}
            onChange={(e) => setNuevaCuentaOrigen((p) => ({ ...p, nombre: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="nueva-cuenta-origen-tipo">{isSpanish ? 'Tipo' : 'Type'}</InputLabel>
            <Select
              labelId="nueva-cuenta-origen-tipo"
              label={isSpanish ? 'Tipo' : 'Type'}
              value={nuevaCuentaOrigen.tipo}
              onChange={(e) => setNuevaCuentaOrigen((p) => ({ ...p, tipo: e.target.value }))}
            >
              <MenuItem value="ahorros">{isSpanish ? 'Ahorros' : 'Savings'}</MenuItem>
              <MenuItem value="corriente">{isSpanish ? 'Corriente' : 'Checking'}</MenuItem>
              <MenuItem value="efectivo">{isSpanish ? 'Efectivo' : 'Cash'}</MenuItem>
              <MenuItem value="tarjeta_credito">{isSpanish ? 'Tarjeta de crédito' : 'Credit card'}</MenuItem>
              <MenuItem value="prestamo">{isSpanish ? 'Préstamo' : 'Loan'}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="nueva-cuenta-origen-moneda">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
              <Select
                labelId="nueva-cuenta-origen-moneda"
                label={isSpanish ? 'Moneda' : 'Currency'}
                value={nuevaCuentaOrigen.moneda}
                onChange={(e) => setNuevaCuentaOrigen((p) => ({ ...p, moneda: e.target.value }))}
              >
                <MenuItem value="PEN">PEN</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label={isSpanish ? 'Saldo actual' : 'Current balance'}
              value={nuevaCuentaOrigen.balance}
              onChange={(e) => setNuevaCuentaOrigen((p) => ({ ...p, balance: e.target.value }))}
              sx={{ flex: 1 }}
            />
          </Box>
          {/* El saldo importa: si queda en 0 y desde acá sale un pago, el
              import rebota con "saldo insuficiente" -- que es justamente
              lo que pasó y motivó este cambio. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {isSpanish
              ? 'Si de esta cuenta va a salir un pago, pon el saldo que tenía: con 0 la importación se rechaza por saldo insuficiente.'
              : 'If a payment comes out of this account, enter its balance: with 0 the import is rejected for insufficient funds.'}
          </Typography>
          {errorCuentaOrigen && <Alert severity="error" sx={{ mt: 2 }}>{errorCuentaOrigen}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNuevaCuentaOrigen(false)}>
            {isSpanish ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardarCuentaOrigen}
            disabled={guardandoCuentaOrigen || !nuevaCuentaOrigen.nombre.trim()}
          >
            {guardandoCuentaOrigen
              ? (isSpanish ? 'Creando…' : 'Creating…')
              : (isSpanish ? 'Crear y usar' : 'Create and use')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Diálogo de edición */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isSpanish ? 'Editar Transacción' : 'Edit Transaction'}
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {currentTransaction && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isSpanish ? 'Descripción:' : 'Description:'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {currentTransaction.description}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                {isSpanish ? 'Fecha:' : 'Date:'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {currentTransaction.date}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                {isSpanish ? 'Monto:' : 'Amount:'}
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                color={currentTransaction.transaction_type === 'income' ? 'success.main' : 'error.main'}
              >
                {currentTransaction.transaction_type === 'income' ? '+' : '-'}
                {getRowCurrencySymbol(currentTransaction)}{Math.abs(currentTransaction.amount).toFixed(2)}
                {currentTransaction.moneda_override && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({currentTransaction.moneda_override})
                  </Typography>
                )}
              </Typography>
              {/* 21 sep 2026 (Augusto: "no me da opción alguna para hacer
                  algo con esta supuesta operación duplicada"). El aviso
                  era un callejón sin salida: decía que había un duplicado
                  pero no CUÁL, y no ofrecía ninguna acción -- había que
                  cerrar, buscar la fila en la tabla y acordarse de
                  tildarla. Ahora muestra contra qué choca y deja decidir
                  desde acá mismo. */}
              {currentTransaction.posible_duplicado && (
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }} icon={<WarningAmberIcon fontSize="small" />}>
                  <Typography variant="body2" sx={{ mb: currentTransaction.duplicado_de ? 1 : 0 }}>
                    {isSpanish
                      ? 'Se parece a una transacción que ya tienes.'
                      : 'This looks like a transaction you already have.'}
                  </Typography>
                  {currentTransaction.duplicado_de && (
                    <Box
                      sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {isSpanish ? 'Ya registrada:' : 'Already recorded:'}
                      </Typography>
                      <Typography variant="body2">
                        {currentTransaction.duplicado_de.fecha}
                        {' · '}
                        {currentTransaction.duplicado_de.moneda || ''} {Number(currentTransaction.duplicado_de.monto).toFixed(2)}
                        {currentTransaction.duplicado_de.proveedor ? ` · ${currentTransaction.duplicado_de.proveedor}` : ''}
                      </Typography>
                      {currentTransaction.duplicado_de.descripcion && (
                        <Typography variant="caption" color="text.secondary">
                          {currentTransaction.duplicado_de.descripcion}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {isSpanish
                      ? 'Si son dos movimientos distintos que casualmente coinciden, impórtalo igual.'
                      : 'If these are two different movements that happen to match, import it anyway.'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant={selectedRows.includes(currentTransaction.id) ? 'contained' : 'outlined'}
                      color="warning"
                      onClick={() =>
                        setSelectedRows((prev) =>
                          prev.includes(currentTransaction.id) ? prev : [...prev, currentTransaction.id]
                        )
                      }
                    >
                      {isSpanish ? 'Importarla igual' : 'Import it anyway'}
                    </Button>
                    <Button
                      size="small"
                      variant={!selectedRows.includes(currentTransaction.id) ? 'contained' : 'outlined'}
                      color="inherit"
                      onClick={() =>
                        setSelectedRows((prev) => prev.filter((id) => id !== currentTransaction.id))
                      }
                    >
                      {isSpanish ? 'Dejarla fuera' : 'Leave it out'}
                    </Button>
                  </Box>
                </Alert>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                {isSpanish ? 'Proveedor:' : 'Merchant:'}
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder={isSpanish ? 'Ej. Wong, Uber, Netflix…' : 'E.g. Wong, Uber, Netflix…'}
                value={currentTransaction.suggested_proveedor || ''}
                onChange={(e) => handleProveedorChange(e.target.value)}
                helperText={
                  isSpanish
                    ? 'Se recuerda para futuras importaciones de este mismo proveedor.'
                    : 'Remembered for future imports of this same merchant.'
                }
              />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                {isSpanish ? 'Selecciona la categoría:' : 'Select category:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                {allCategories.map((category) => (
                  <CategoryChip
                    key={category.value}
                    label={category.label}
                    icon={getCategoryIcon(category.value)}
                    color={getCategoryColor(category.value)}
                    onClick={() => handleCategoryChange(category.value)}
                    sx={{
                      m: 0.5,
                      bgcolor: currentTransaction.suggested_category === category.value
                        ? alpha(getCategoryColor(category.value), 0.2)
                        : 'transparent',
                      border: `1px solid ${currentTransaction.suggested_category === category.value
                        ? getCategoryColor(category.value)
                        : theme.palette.divider}`
                    }}
                  />
                ))}
              </Box>
              {/* 5 ago 2026: ya no hay un botón "Crear" aparte -- basta con
                  escribir acá y presionar "Guardar" (abajo), que crea Y
                  asigna la categoría nueva en un solo paso (ver
                  handleSaveEdit). Se guarda en el usuario
                  (endpoints/categories.py) y queda disponible en el resto
                  de la app. */}
              <TextField
                size="small"
                fullWidth
                placeholder={isSpanish ? 'O escribe una categoría nueva…' : 'Or type a new category…'}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveEdit();
                  }
                }}
                helperText={
                  isSpanish
                    ? 'Presiona Guardar para crearla y asignarla a esta transacción.'
                    : 'Press Save to create it and assign it to this transaction.'
                }
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            {isSpanish ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={savingCategory}
            startIcon={savingCategory ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: goldColors.main,
              '&:hover': { bgcolor: goldColors.dark }
            }}
          >
            {isSpanish ? 'Guardar' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 4 ago 2026: comprar un paquete de páginas extra (ver
          backend_new/app/core/plans.py) -- pago único vía Mercado Pago o
          PayPal, mismo mecanismo que la suscripción de planes
          (Subscription.js), pero acá el pago no cambia de plan: solo
          suma páginas a User.extra_pages_balance cuando el webhook lo
          confirma. Redirige fuera de la app (window.location.href), así
          que al volver el importador ya se cierra -- el cliente lo puede
          reabrir desde el header y la cuota va a reflejar el nuevo saldo. */}
      <Dialog open={buyDialogOpen} onClose={() => setBuyDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isSpanish ? 'Comprar páginas extra' : 'Buy extra pages'}
          <IconButton
            aria-label="close"
            onClick={() => setBuyDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSpanish
              ? 'Las páginas que compres no caducan — se quedan disponibles hasta que las uses.'
              : "Pages you buy don't expire — they stay available until you use them."}
          </Typography>
          {pagePacks.map((pack) => (
            <Box
              key={pack.id}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 2, mb: 2 }}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                {pack.paginas} {isSpanish ? 'páginas extra' : 'extra pages'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                ${pack.precio_usd} USD · S/ {pack.precio_pen} PEN
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={buyingPackId === `${pack.id}-mercadopago`}
                  onClick={() => handleBuyPack(pack.id, 'mercadopago')}
                  sx={{ bgcolor: goldColors.main, '&:hover': { bgcolor: goldColors.dark } }}
                >
                  {buyingPackId === `${pack.id}-mercadopago`
                    ? (isSpanish ? 'Redirigiendo…' : 'Redirecting…')
                    : 'Mercado Pago'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={buyingPackId === `${pack.id}-paypal`}
                  onClick={() => handleBuyPack(pack.id, 'paypal')}
                >
                  {buyingPackId === `${pack.id}-paypal`
                    ? (isSpanish ? 'Redirigiendo…' : 'Redirecting…')
                    : 'PayPal'}
                </Button>
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};
export default StatementImporter;
